import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  LayoutDashboard, Radio, Flag, Settings, Sun, Moon, MessageSquare,
  TrendingUp, Users, Video, Activity, Award, AlertCircle, CheckCircle,
  Clock, Eye, ThumbsUp, Search, Filter, MoreVertical, Edit, Trash2,
  Star, Flame, Sparkles, BarChart3, Calendar, Download, RefreshCw, Play,
  Upload, Plus, Key, Copy, ExternalLink, UserCheck, UserX, Database, X as CloseIcon,
  Save, Check, Lock, Unlock, Pin, EyeOff, Film, Tv, Music, Disc, FlaskConical, ChevronRight, Zap, PieChart
} from 'lucide-react';
import { adminAPI, adminFeaturedAPI, forumAPI, handleAPIError } from '../services/api';
import axios from 'axios';
import AdminPeakMomentsTab from './AdminPeakMomentsTab';
import AdminPeakCandidatesTab from './AdminPeakCandidatesTab';
import AdminMediaContentTab from './AdminMediaContentTab';
import AdminVotingTab from './AdminVotingTab';
import AdminAIChat from './AdminAIChat';
import AdminCreatorsTab from './AdminCreatorsTab';
import AdminTestingPanel from './AdminTestingPanel';
import AdminPlatformAnalyticsTab from './AdminPlatformAnalyticsTab';

// ─────────────────────────────────────────────────────────────────────────────
// Channel Management — standalone component.
// All search / filter / sort / page state lives HERE so that a keystroke never
// causes the entire 3 000-line dashboard to re-render.
// ─────────────────────────────────────────────────────────────────────────────
const _chSelectStyle = (colors) => ({
  padding: '9px 14px', borderRadius: '8px', border: `1px solid ${colors.border}`,
  backgroundColor: colors.bgSecondary, color: colors.text,
  fontSize: '13px', cursor: 'pointer', outline: 'none',
});

const _PagBtn = ({ onClick, disabled, children, colors }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: '6px 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
    backgroundColor: colors.bgSecondary, color: disabled ? colors.textTertiary : colors.text,
    fontSize: '13px', fontWeight: '500', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
  }}>{children}</button>
);

const CH_CAT_COLORS = {
  movies: '#8B5CF6', tv: '#3B82F6', anime: '#F97316',
  gaming: '#10B981', music: '#EC4899', general: '#6B7280',
};
const CH_TYPE_COLORS = {
  solo: '#8B5CF6', group: '#3B82F6', duo: '#10B981',
  network: '#F97316', organization: '#EF4444',
};
const fmtSubs = (n) => {
  if (!n) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return Math.round(n / 1_000) + 'K';
  return n.toLocaleString();
};

const ADMIN_MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'channels', label: 'Channels', icon: Radio },
  { id: 'peak-moments', label: 'Peak Moments', icon: Play },
  { id: 'peak-candidates', label: 'Moment Candidates', icon: Sparkles },
  { id: 'video-ingestion', label: 'Video Ingestion', icon: Upload },
  { id: 'media-content', label: 'Media Content', icon: Film },
  { id: 'featured', label: 'Explore Rankings', icon: Star },
  { id: 'voting', label: 'Voting & Polls', icon: ThumbsUp },
  { id: 'forum', label: 'Forum & Threads', icon: MessageSquare },
  { id: 'reports', label: 'Reports', icon: Flag },
  { id: 'creators', label: 'Creator Accounts', icon: UserCheck },
  { id: 'platform-analytics', label: 'Platform Analytics', icon: PieChart },
  { id: 'boosts', label: 'Discovery Boosts', icon: Zap },
  { id: 'testing', label: 'Testing Panel', icon: FlaskConical },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const ChannelsView = ({ channels = [], colors, onAdd, onEdit, onRemove }) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [catFilter, setCatFilter]     = useState('all');
  const [typeFilter, setTypeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy]   = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const debounceRef = useRef(null);

  // Instant UI update; deferred filter (300 ms) — eliminates typing lag
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearchQuery(val); setPage(1); }, 300);
  };

  const setFilter = (setter) => (v) => { setter(v); setPage(1); };
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };

  const filtered = useMemo(() => {
    let list = channels;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(ch =>
        ch.name?.toLowerCase().includes(q) || ch.id?.toLowerCase().includes(q)
      );
    }
    if (catFilter !== 'all')    list = list.filter(ch => ch.categories?.includes(catFilter));
    if (typeFilter !== 'all')   list = list.filter(ch => ch.reactorType === typeFilter);
    if (statusFilter !== 'all') list = list.filter(ch => (ch.status || 'active') === statusFilter);
    return [...list].sort((a, b) => {
      let vA, vB;
      if (sortBy === 'name')        { vA = a.name?.toLowerCase() || ''; vB = b.name?.toLowerCase() || ''; }
      else if (sortBy === 'subscribers') { vA = a.subscribers || 0; vB = b.subscribers || 0; }
      else                          { vA = a.lastActive || ''; vB = b.lastActive || ''; }
      if (typeof vA === 'number') return sortDir === 'asc' ? vA - vB : vB - vA;
      return sortDir === 'asc' ? vA.localeCompare(vB) : vB.localeCompare(vA);
    });
  }, [channels, searchQuery, catFilter, typeFilter, statusFilter, sortBy, sortDir]);

  const stats = useMemo(() => {
    const total    = channels.length;
    const active   = channels.filter(ch => (ch.status || 'active') === 'active').length;
    const withId   = channels.filter(ch => !!ch.id).length;
    return { total, active, inactive: total - active, withId };
  }, [channels]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const thBase = {
    padding: '13px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    backgroundColor: colors.bgTertiary, userSelect: 'none',
  };
  const SortTh = ({ col, label }) => (
    <th onClick={() => toggleSort(col)} style={{
      ...thBase, cursor: 'pointer',
      color: sortBy === col ? colors.accent : colors.textSecondary,
    }}>
      {label}{' '}
      <span style={{ opacity: sortBy === col ? 1 : 0.25 }}>
        {sortBy === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );

  const pageWindow = (() => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
  })();

  const hasActiveFilters = searchQuery || catFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all';

  return (
    <div style={{ padding: '32px', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: colors.text, margin: '0 0 4px 0' }}>
            Channel Management
          </h1>
          <p style={{ fontSize: '14px', color: colors.textSecondary, margin: 0 }}>
            {stats.total.toLocaleString()} channels · {stats.active.toLocaleString()} active
          </p>
        </div>
        <button onClick={onAdd} style={{
          padding: '10px 20px', borderRadius: '8px', border: 'none',
          backgroundColor: colors.success, color: '#FFF',
          fontSize: '14px', fontWeight: '600', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <Plus size={15} /> Add Channel
        </button>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total Channels',   value: stats.total,    color: colors.accent },
          { label: 'Active',           value: stats.active,   color: colors.success },
          { label: 'Inactive',         value: stats.inactive, color: colors.warning },
          { label: 'With Channel ID',  value: stats.withId,   color: colors.info },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`,
            borderRadius: '10px', padding: '16px 20px', borderLeft: `4px solid ${color}`,
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color, lineHeight: 1 }}>
              {value.toLocaleString()}
            </div>
            <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '6px' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search — uses local `searchInput` so UI is instant */}
        <div style={{ flex: 1, minWidth: '240px', maxWidth: '380px', position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary }} />
          <input
            type="text"
            placeholder="Search by name or channel ID…"
            value={searchInput}
            onChange={handleSearch}
            style={{
              width: '100%', padding: '9px 14px 9px 38px',
              borderRadius: '8px', border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgSecondary, color: colors.text,
              fontSize: '13px', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        <select value={catFilter}    onChange={e => setFilter(setCatFilter)(e.target.value)}    style={_chSelectStyle(colors)}>
          <option value="all">All Categories</option>
          <option value="general">General</option>
          <option value="movies">Movies</option>
          <option value="tv">TV Shows</option>
          <option value="anime">Anime</option>
          <option value="gaming">Gaming</option>
          <option value="music">Music</option>
        </select>

        <select value={typeFilter}   onChange={e => setFilter(setTypeFilter)(e.target.value)}   style={_chSelectStyle(colors)}>
          <option value="all">All Types</option>
          <option value="solo">Solo</option>
          <option value="group">Group</option>
          <option value="duo">Duo</option>
          <option value="network">Network</option>
        </select>

        <select value={statusFilter} onChange={e => setFilter(setStatusFilter)(e.target.value)} style={_chSelectStyle(colors)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {hasActiveFilters && (
          <button
            onClick={() => { setSearchInput(''); setSearchQuery(''); setCatFilter('all'); setTypeFilter('all'); setStatusFilter('all'); setPage(1); }}
            style={{
              padding: '9px 14px', borderRadius: '8px', border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent', color: colors.textSecondary,
              fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <CloseIcon size={13} /> Clear
          </button>
        )}
      </div>

      {/* ── Results info + top pagination ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ fontSize: '13px', color: colors.textSecondary }}>
          {filtered.length === 0
            ? 'No channels match your filters'
            : `Showing ${((page - 1) * PAGE_SIZE + 1).toLocaleString()}–${Math.min(page * PAGE_SIZE, filtered.length).toLocaleString()} of ${filtered.length.toLocaleString()} channels`}
          {filtered.length < stats.total ? ` · filtered from ${stats.total.toLocaleString()}` : ''}
        </span>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <_PagBtn disabled={page === 1}          onClick={() => setPage(p => p - 1)} colors={colors}>‹ Prev</_PagBtn>
            <span style={{ fontSize: '13px', color: colors.textSecondary, padding: '0 10px' }}>{page} / {totalPages}</span>
            <_PagBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)} colors={colors}>Next ›</_PagBtn>
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        {paginated.length === 0 ? (
          <div style={{ padding: '80px 40px', textAlign: 'center', color: colors.textSecondary }}>
            <Search size={40} style={{ opacity: 0.2, display: 'block', margin: '0 auto 16px' }} />
            <p style={{ margin: 0, fontSize: '15px' }}>No channels match your filters</p>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: colors.textTertiary }}>Try adjusting the search or filter criteria</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thBase, color: colors.textSecondary, width: '52px' }}>#</th>
                <SortTh col="name" label="Channel" />
                <th style={{ ...thBase, color: colors.textSecondary }}>Categories</th>
                <th style={{ ...thBase, color: colors.textSecondary }}>Type</th>
                <th style={{ ...thBase, color: colors.textSecondary }}>Status</th>
                <SortTh col="subscribers" label="Subscribers" />
                <th style={{ ...thBase, color: colors.textSecondary, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((ch, idx) => {
                const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
                const avatar = ch.avatar || ch.thumbnail;
                const cats   = ch.categories || [];
                const status = ch.status || 'active';
                return (
                  <tr
                    key={ch.id || idx}
                    style={{ borderTop: `1px solid ${colors.border}`, transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgTertiary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* # */}
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: colors.textTertiary }}>{rowNum}</td>

                    {/* Channel */}
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {avatar
                          ? <img src={avatar} alt={ch.name} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                          : null}
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          backgroundColor: colors.accent + '30',
                          display: avatar ? 'none' : 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          color: colors.accent, fontSize: '15px', fontWeight: '700', flexShrink: 0,
                        }}>
                          {ch.name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '260px' }}>
                            {ch.name || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textTertiary, fontFamily: 'monospace', marginTop: '2px' }}>
                            {ch.id || '—'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Categories */}
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '180px' }}>
                        {cats.slice(0, 2).map(cat => (
                          <span key={cat} style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                            backgroundColor: (CH_CAT_COLORS[cat] || '#6B7280') + '20',
                            color: CH_CAT_COLORS[cat] || '#6B7280',
                          }}>{cat}</span>
                        ))}
                        {cats.length > 2 && (
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', color: colors.textTertiary, backgroundColor: colors.bgTertiary }}>
                            +{cats.length - 2}
                          </span>
                        )}
                        {cats.length === 0 && <span style={{ fontSize: '12px', color: colors.textTertiary }}>—</span>}
                      </div>
                    </td>

                    {/* Type */}
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: (CH_TYPE_COLORS[ch.reactorType] || '#6B7280') + '20',
                        color: CH_TYPE_COLORS[ch.reactorType] || '#6B7280',
                      }}>
                        {ch.reactorType || 'solo'}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: status === 'active' ? colors.success + '20' : colors.warning + '20',
                        color: status === 'active' ? colors.success : colors.warning,
                      }}>
                        {status}
                      </span>
                    </td>

                    {/* Subscribers */}
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: colors.text }}>
                      {fmtSubs(ch.subscribers)}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button onClick={() => onEdit(ch)} title="Edit" style={{ padding: '7px', borderRadius: '6px', border: 'none', backgroundColor: colors.accent + '20', color: colors.accent, cursor: 'pointer' }}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => { if (window.confirm(`Remove "${ch.name}"?`)) onRemove(ch.name); }} title="Delete" style={{ padding: '7px', borderRadius: '6px', border: 'none', backgroundColor: colors.error + '20', color: colors.error, cursor: 'pointer' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Bottom pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '24px', flexWrap: 'wrap' }}>
          <_PagBtn disabled={page === 1}          onClick={() => setPage(1)}             colors={colors}>« First</_PagBtn>
          <_PagBtn disabled={page === 1}          onClick={() => setPage(p => p - 1)}    colors={colors}>‹ Prev</_PagBtn>
          {pageWindow.map(p => (
            <button key={p} onClick={() => setPage(p)} style={{
              padding: '6px 13px', borderRadius: '6px',
              border: p === page ? 'none' : `1px solid ${colors.border}`,
              backgroundColor: p === page ? colors.accent : colors.bgSecondary,
              color: p === page ? '#FFF' : colors.text,
              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}>{p}</button>
          ))}
          <_PagBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}    colors={colors}>Next ›</_PagBtn>
          <_PagBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}     colors={colors}>Last »</_PagBtn>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────
// Admin Boosts View
// ─────────────────────────────────────────────────────────────────────────────

const statusColor = { pending_payment: '#888', active: '#10B981', expired: '#555', cancelled: '#EF4444' };
const videoStatusColor = { pending: '#FBBF24', approved: '#10B981', rejected: '#EF4444' };

const AdminBoostsView = ({ colors, showAlert }) => {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [expanded, setExpanded] = useState({});
  const [coolOffInput, setCoolOffInput] = useState({});

  const fetchBoosts = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getBoosts(filter || undefined);
      setBoosts(data.boosts || []);
    } catch { setBoosts([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBoosts(); }, [filter]);

  const cancel = async (id) => {
    try {
      await adminAPI.cancelBoost(id);
      showAlert({ type: 'success', message: 'Boost cancelled.' });
      fetchBoosts();
    } catch { showAlert({ type: 'error', message: 'Could not cancel boost.' }); }
  };

  const approveVideo = async (boostId, videoId) => {
    const hours = coolOffInput[videoId] || 24;
    try {
      await adminAPI.approveBoostVideo(boostId, videoId, hours);
      showAlert({ type: 'success', message: 'Video approved and added to carousel.' });
      fetchBoosts();
    } catch (e) { showAlert({ type: 'error', message: e?.response?.data?.error || 'Approval failed.' }); }
  };

  const rejectVideo = async (boostId, videoId) => {
    try {
      await adminAPI.rejectBoostVideo(boostId, videoId);
      showAlert({ type: 'success', message: 'Video rejected.' });
      fetchBoosts();
    } catch { showAlert({ type: 'error', message: 'Could not reject video.' }); }
  };

  const setCoolOff = async (featuredId, videoId) => {
    const hours = coolOffInput[videoId] || 24;
    try {
      await adminAPI.setCarouselCoolOff(featuredId, hours);
      showAlert({ type: 'success', message: `Cool-off set to ${hours}h.` });
      fetchBoosts();
    } catch { showAlert({ type: 'error', message: 'Could not set cool-off.' }); }
  };

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors?.text || '#E5E5E5', margin: 0 }}>Discovery Boosts</h2>
          <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>Review carousel video submissions and manage cool-off periods</p>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{
          background: '#111', border: '1px solid #2A2A3E', color: '#E5E5E5',
          padding: '8px 12px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
        }}>
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#555' }}>Loading...</div>
      ) : boosts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'rgba(255,255,255,0.02)', border: '1px solid #1A1A2E', borderRadius: '12px', color: '#555' }}>
          <Zap size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
          <div>No boosts found.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {boosts.map(b => {
            const isExpanded = expanded[b._id];
            const videos = b.carouselVideos || [];
            const pendingCount = videos.filter(v => v.approvalStatus === 'pending').length;

            return (
              <div key={b._id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid #1E1E30', borderRadius: '12px', overflow: 'hidden' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#FFF' }}>{b.channelName || b.channelId}</span>
                      <span style={{
                        fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px',
                        background: `${statusColor[b.status]}22`, color: statusColor[b.status],
                        border: `1px solid ${statusColor[b.status]}44`, padding: '2px 8px', borderRadius: '4px',
                      }}>{b.status.toUpperCase()}</span>
                      <span style={{ fontSize: '12px', color: '#666' }}>{b.billingPeriod} · ${b.price?.toFixed(2)}</span>
                      {b.overlayImpressions > 0 && (
                        <span style={{ fontSize: '12px', color: '#A78BFA' }}>⚡ {b.overlayImpressions} impressions</span>
                      )}
                      {pendingCount > 0 && (
                        <span style={{ fontSize: '10px', fontWeight: '700', background: 'rgba(251,191,36,0.15)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.3)', padding: '2px 8px', borderRadius: '4px' }}>
                          {pendingCount} pending review
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>
                      {b.startDate && `Active: ${new Date(b.startDate).toLocaleDateString()} → ${new Date(b.endDate).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {videos.length > 0 && (
                      <button onClick={() => setExpanded(e => ({ ...e, [b._id]: !isExpanded }))} style={{
                        background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)',
                        color: '#A78BFA', padding: '7px 14px', borderRadius: '7px',
                        cursor: 'pointer', fontSize: '13px',
                      }}>
                        {isExpanded ? 'Hide Videos' : `Videos (${videos.length})`}
                      </button>
                    )}
                    {b.status === 'active' && (
                      <button onClick={() => cancel(b._id)} style={{
                        background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                        color: '#F87171', padding: '7px 14px', borderRadius: '7px',
                        cursor: 'pointer', fontSize: '13px',
                      }}>Cancel</button>
                    )}
                  </div>
                </div>

                {/* Videos panel */}
                {isExpanded && videos.length > 0 && (
                  <div style={{ borderTop: '1px solid #1A1A2E', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {videos.map(v => (
                      <div key={v._id} style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        background: 'rgba(255,255,255,0.02)', border: '1px solid #1A1A2E',
                        borderRadius: '10px', padding: '12px 14px',
                      }}>
                        {v.videoId ? (
                          <img src={`https://img.youtube.com/vi/${v.videoId}/mqdefault.jpg`} alt="" style={{ width: '96px', height: '54px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 96, height: 54, borderRadius: 6, background: '#111', flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', color: '#E5E5E5', fontWeight: '500', marginBottom: '2px' }}>{v.title || 'Untitled'}</div>
                          <div style={{ fontSize: '11px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.url}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                            <span style={{
                              fontSize: '10px', fontWeight: '700',
                              background: `${videoStatusColor[v.approvalStatus]}22`,
                              color: videoStatusColor[v.approvalStatus],
                              border: `1px solid ${videoStatusColor[v.approvalStatus]}44`,
                              padding: '2px 7px', borderRadius: '4px',
                            }}>{v.approvalStatus.toUpperCase()}</span>
                            {v.approvalStatus === 'approved' && (
                              <span style={{ fontSize: '11px', color: '#555' }}>In carousel</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, alignItems: 'flex-end' }}>
                          {v.approvalStatus === 'pending' && (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '11px', color: '#666' }}>Cool-off (h):</span>
                                <input
                                  type="number" min="1" max="168" defaultValue={24}
                                  onChange={e => setCoolOffInput(c => ({ ...c, [v._id]: parseInt(e.target.value) || 24 }))}
                                  style={{ width: '52px', background: '#111', border: '1px solid #2A2A3E', color: '#E5E5E5', borderRadius: '5px', padding: '3px 6px', fontSize: '12px' }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={() => approveVideo(b._id, v._id)} style={{
                                  background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                                  color: '#10B981', padding: '6px 12px', borderRadius: '6px',
                                  cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                                }}>Approve</button>
                                <button onClick={() => rejectVideo(b._id, v._id)} style={{
                                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                                  color: '#F87171', padding: '6px 12px', borderRadius: '6px',
                                  cursor: 'pointer', fontSize: '12px',
                                }}>Reject</button>
                              </div>
                            </>
                          )}
                          {v.approvalStatus === 'approved' && v.featuredContentId && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '11px', color: '#666' }}>Cool-off (h):</span>
                              <input
                                type="number" min="1" max="168" defaultValue={24}
                                onChange={e => setCoolOffInput(c => ({ ...c, [v._id]: parseInt(e.target.value) || 24 }))}
                                style={{ width: '52px', background: '#111', border: '1px solid #2A2A3E', color: '#E5E5E5', borderRadius: '5px', padding: '3px 6px', fontSize: '12px' }}
                              />
                              <button onClick={() => setCoolOff(v.featuredContentId, v._id)} style={{
                                background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)',
                                color: '#A78BFA', padding: '5px 10px', borderRadius: '6px',
                                cursor: 'pointer', fontSize: '12px',
                              }}>Set</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ModernAdminDashboard = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [exploreSection, setExploreSection] = useState('explore-rankings-section');
  const [aiChatExpanded, setAiChatExpanded] = useState(false);

  // Old dashboard state - REAL data
  const [data, setData] = useState({ channels: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [newChannelId, setNewChannelId] = useState('');
  const [customName, setCustomName] = useState('');
  const [adding, setAdding] = useState(false);

  // Video Ingestion
  const [ingestionChannels, setIngestionChannels] = useState([]);
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [selectedIngestionChannelIds, setSelectedIngestionChannelIds] = useState(new Set());

  // Featured Content
  const [featuredChannels, setFeaturedChannels] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [carouselItems, setCarouselItems] = useState([]);
  const [featuredChannelsList, setFeaturedChannelsList] = useState([]);
  const [featuredAudience, setFeaturedAudience] = useState('general');
  const [carouselAutomationSettings, setCarouselAutomationSettings] = useState(null);
  const [savingCarouselSettings, setSavingCarouselSettings] = useState(false);
  const [savingCarouselOrder, setSavingCarouselOrder] = useState(false);
  const [updatingCarouselEligibility, setUpdatingCarouselEligibility] = useState('');
  const [carouselChannelSearch, setCarouselChannelSearch] = useState('');
  const [carouselChannelListExpanded, setCarouselChannelListExpanded] = useState(false);
  const [minDurationMinutesInput, setMinDurationMinutesInput] = useState('4');
  const [minDurationSynced, setMinDurationSynced] = useState(false);

  // Featured of the Week selections
  const [channelOfWeek, setChannelOfWeek] = useState('');
  const [risingStar, setRisingStar] = useState('');
  const [mostTalked, setMostTalked] = useState('');
  const [staffPick, setStaffPick] = useState('');
  const [featuredMovieId, setFeaturedMovieId] = useState('');
  const [featuredTvShowId, setFeaturedTvShowId] = useState('');
  const [featuredMusicId, setFeaturedMusicId] = useState('');
  const [mostWantedIds, setMostWantedIds] = useState(['', '', '']);

  // Smart Suggestions
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Reaction of the Week videos
  const [reactionVideo1, setReactionVideo1] = useState('');
  const [reactionVideo2, setReactionVideo2] = useState('');
  const [reactionVideo3, setReactionVideo3] = useState('');

  // Database search modal for carousel
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchMode, setSearchMode] = useState('url'); // 'url' or 'channel'
  const [urlInput, setUrlInput] = useState('');
  const [channelSearchTerm, setChannelSearchTerm] = useState('');
  const [videoSearchTerm, setVideoSearchTerm] = useState('');
  const [videoResults, setVideoResults] = useState([]);
  const [channelResults, setChannelResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSearchChannel, setSelectedSearchChannel] = useState(null);

  // Forum/Threads management
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadFilter, setThreadFilter] = useState('all'); // 'all', 'pinned', 'locked', 'hidden'
  const [threadSearch, setThreadSearch] = useState('');

  // Media Content Ingestion
  const [mediaContent, setMediaContent] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaStats, setMediaStats] = useState(null);
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all'); // 'all', 'movie', 'series', 'anime', 'music'
  const [mediaSearchTerm, setMediaSearchTerm] = useState('');
  const [ingestModalOpen, setIngestModalOpen] = useState(false);
  const [ingestTitle, setIngestTitle] = useState('');
  const [ingestType, setIngestType] = useState('movie');
  const [ingestAutoApprove, setIngestAutoApprove] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Dashboard overview state
  const [dashboardOverview, setDashboardOverview] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const colors = theme === 'dark' ? {
    bg: '#0A0A0A',
    bgSecondary: '#141414',
    bgTertiary: '#1E1E1E',
    border: '#2A2A2A',
    text: '#FFFFFF',
    textSecondary: '#A0A0A0',
    textTertiary: '#707070',
    accent: '#8B5CF6',
    accentHover: '#7C3AED',
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
  } : {
    bg: '#FFFFFF',
    bgSecondary: '#F9FAFB',
    bgTertiary: '#F3F4F6',
    border: '#E5E7EB',
    text: '#111827',
    textSecondary: '#6B7280',
    textTertiary: '#9CA3AF',
    accent: '#8B5CF6',
    accentHover: '#7C3AED',
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
  };

  // REAL data loading
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllChannelData();
      setData(response.data);
    } catch (error) {
      console.error('❌ Data loading failed:', error);
      setAlert({ type: 'error', message: 'Failed to load data: ' + handleAPIError(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIngestionStatusData = useCallback(async () => {
    if (activeTab !== 'video-ingestion') return;
    setIngestionLoading(true);
    try {
      const response = await adminAPI.getIngestionStatus();
      setIngestionChannels(response.data.channels || []);
    } catch (error) {
      setAlert({ type: 'error', message: 'Video Ingestion: ' + handleAPIError(error) });
    } finally {
      setIngestionLoading(false);
    }
  }, [activeTab]);

  const fetchFeaturedData = useCallback(async () => {
    if (activeTab !== 'featured') return;
    setFeaturedLoading(true);
    setSuggestionsLoading(true);
    try {
      const [channelsResponse, featuredResponse, rankingsResponse, suggestionsResponse, mediaLibraryResponse, reactionOfWeekResponse, automationSettingsResponse] = await Promise.all([
        adminAPI.getAllChannelData(),
        adminFeaturedAPI.getAllFeatured(featuredAudience),
        adminAPI.getSidebarRankings(),
        adminAPI.getFeaturedSuggestions().catch(() => null),
        adminAPI.getMediaLibrary({ limit: 300 }).catch(() => ({ results: [] })),
        adminAPI.getReactionOfWeek(featuredAudience).catch(() => ({ data: { videos: [] } })),
        adminAPI.getCarouselAutomationSettings().catch(() => ({ data: { settings: null } }))
      ]);

      const channels = channelsResponse.data.channels || [];
      const featuredData = featuredResponse.data.data || { carousel: [], channels: [] };

      setFeaturedChannels(channels);
      setCarouselItems(featuredData.carousel || []);
      setFeaturedChannelsList(featuredData.channels || []);
      setCarouselAutomationSettings(automationSettingsResponse.data?.settings || null);
      if (!minDurationSynced) {
        const loadedMinSeconds = automationSettingsResponse.data?.settings?.minDurationSeconds;
        if (Number.isFinite(loadedMinSeconds)) {
          setMinDurationMinutesInput(String(Math.round((loadedMinSeconds / 60) * 10) / 10));
        }
        setMinDurationSynced(true);
      }
      setMediaContent(mediaLibraryResponse.results || []);
      const reactionVideos = reactionOfWeekResponse.data?.videos || [];
      setReactionVideo1(reactionVideos.find(v => v.slot === 1)?.videoUrl || '');
      setReactionVideo2(reactionVideos.find(v => v.slot === 2)?.videoUrl || '');
      setReactionVideo3(reactionVideos.find(v => v.slot === 3)?.videoUrl || '');

      // Load current sidebar rankings
      if (rankingsResponse.data?.success && rankingsResponse.data?.rankings) {
        const rankings = rankingsResponse.data.rankings;
        setChannelOfWeek(rankings.channelOfWeek?.channelId || '');
        setRisingStar(rankings.risingStar?.channelId || '');
        setMostTalked(rankings.mostTalkedAbout?.channelId || '');
        setStaffPick(rankings.staffPick?.channelId || '');
        setFeaturedMovieId(rankings.featuredMovie?.dbId || rankings.featuredMovie?.contentId || '');
        setFeaturedTvShowId(rankings.featuredTvShow?.dbId || rankings.featuredTvShow?.contentId || '');
        setFeaturedMusicId(rankings.featuredMusic?.dbId || rankings.featuredMusic?.contentId || '');
        setMostWantedIds(
          [0, 1, 2].map(index => rankings.mostWanted?.[index]?.dbId || rankings.mostWanted?.[index]?.contentId || '')
        );
      }

      if (suggestionsResponse?.success) {
        setSuggestions(suggestionsResponse);
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Featured Content: ' + handleAPIError(error) });
    } finally {
      setFeaturedLoading(false);
      setSuggestionsLoading(false);
    }
  }, [activeTab, featuredAudience]);

  const fetchDashboardOverview = useCallback(async () => {
    if (activeTab !== 'dashboard') return;
    setDashboardLoading(true);
    try {
      const response = await adminAPI.getDashboardOverview();
      setDashboardOverview(response.data?.overview || null);
    } catch (error) {
      console.error('Dashboard overview error:', error);
    } finally {
      setDashboardLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
    if (activeTab === 'dashboard') {
      fetchDashboardOverview();
    } else if (activeTab === 'video-ingestion') {
      fetchIngestionStatusData();
    } else if (activeTab === 'featured') {
      fetchFeaturedData();
    }
  }, [activeTab, loadData, fetchDashboardOverview, fetchIngestionStatusData, fetchFeaturedData]);


  // Channel management functions
  const addNewChannel = async (channelId, options) => {
    try {
      console.log('🚀 Adding channel:', channelId, options);
      await adminAPI.addChannelById(channelId, options);
      setAlert({ type: 'success', message: 'Channel added successfully!' });
      await loadData();
      setModalOpen(false);
    } catch (error) {
      console.error('❌ Add channel error:', error);
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const removeChannel = async (channelName) => {
    try {
      await adminAPI.removeFromWhitelist(channelName);
      setAlert({ type: 'success', message: `Removed ${channelName}` });
      loadData();
    } catch (error) {
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const editChannel = async (channelName, updates) => {
    try {
      await adminAPI.updateChannelCategories(
        channelName,
        updates.categories,
        updates.primaryCategory,
        updates.reactorType,
        updates.autoCarouselEligible
      );
      setAlert({ type: 'success', message: 'Channel updated!' });
      loadData();
    } catch (error) {
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const setFeaturedOfWeek = async (type, channelId) => {
    if (!channelId) {
      setAlert({ type: 'error', message: 'Please select a channel' });
      return;
    }
    try {
      console.log(`Setting ${type} to channel:`, channelId);

      // Find the selected channel details
      const selectedCh = (featuredChannels.length > 0 ? featuredChannels : data.channels).find(ch => ch.id === channelId);

      // Map the type to the database position
      const positionMap = {
        'Channel of the Week': 'channelOfWeek',
        'Rising Star': 'risingStar',
        'Most Talked About': 'mostTalkedAbout',
        'Staff Pick': 'staffPick'
      };

      const position = positionMap[type];
      if (!position) {
        setAlert({ type: 'error', message: 'Invalid position type' });
        return;
      }

      // Save to database via API
      const response = await adminAPI.updateSidebarRanking(position, {
        channelId,
        name: selectedCh?.name || 'Unknown',
        thumbnail: selectedCh?.thumbnail || selectedCh?.avatar || selectedCh?.avatarUrl || null
      });

      if (response.data?.success) {
        setAlert({ type: 'success', message: `${type} updated successfully! Visit the Explore tab to see changes.` });
      } else {
        setAlert({ type: 'error', message: response.data?.error || 'Failed to update ranking' });
      }
    } catch (error) {
      console.error('Error updating sidebar ranking:', error);
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const findMediaBySelection = (selectionId) => {
    if (!selectionId) return null;
    return mediaContent.find(media => String(media.dbId || media.id) === String(selectionId)) || null;
  };

  const buildMediaRankingPayload = (media) => ({
    contentId: media.dbId || media.id,
    dbId: media.dbId || media.id,
    title: media.title || '',
    type: media.type || '',
    posterUrl: media.posterUrl || '',
    releaseYear: media.releaseYear || null,
    seasonCount: media.seasonCount || null,
    artist: media.artist || '',
    genre: media.genres?.[0]?.name || '',
    favoriteCount: media.favoriteCount || 0,
    voteCount: media.voteCount || 0
  });

  const saveFeaturedMedia = async (position, selectionId, label) => {
    if (!selectionId) {
      setAlert({ type: 'error', message: `Please select ${label.toLowerCase()}` });
      return;
    }

    const media = findMediaBySelection(selectionId);
    if (!media) {
      setAlert({ type: 'error', message: `${label} could not be found in the approved media library` });
      return;
    }

    try {
      const response = await adminAPI.updateSidebarRanking(position, buildMediaRankingPayload(media));
      if (response.data?.success) {
        setAlert({ type: 'success', message: `${label} updated successfully!` });
      } else {
        setAlert({ type: 'error', message: response.data?.error || `Failed to update ${label.toLowerCase()}` });
      }
    } catch (error) {
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const saveMostWanted = async (slot, selectionId) => {
    if (!selectionId) {
      setAlert({ type: 'error', message: `Please select media for Most Wanted #${slot + 2}` });
      return;
    }

    const media = findMediaBySelection(selectionId);
    if (!media) {
      setAlert({ type: 'error', message: `Most Wanted #${slot + 2} could not be found in the approved media library` });
      return;
    }

    try {
      const response = await adminAPI.updateMostWantedRanking(slot, buildMediaRankingPayload(media));
      if (response.data?.success) {
        setAlert({ type: 'success', message: `Most Wanted #${slot + 2} updated successfully!` });
      } else {
        setAlert({ type: 'error', message: response.data?.error || `Failed to update Most Wanted #${slot + 2}` });
      }
    } catch (error) {
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const saveReactionVideo = async (slot, videoUrl) => {
    if (!videoUrl || !videoUrl.trim()) {
      setAlert({ type: 'error', message: 'Please enter a YouTube video URL' });
      return;
    }

    try {
      console.log(`Saving Reaction Video #${slot}:`, videoUrl);

      const response = await adminAPI.updateReactionOfWeek(slot, videoUrl, featuredAudience);

      if (response.success || response.data?.success) {
        setAlert({ type: 'success', message: `Reaction Video #${slot} saved successfully!` });
        // Optionally refresh the data to show current videos
        fetchFeaturedData();
      } else {
        setAlert({ type: 'error', message: response.error || 'Failed to save video' });
      }
    } catch (error) {
      console.error('Error saving reaction video:', error);
      setAlert({ type: 'error', message: handleAPIError(error) });
    }
  };

  const saveCarouselAutomationSettings = async (updates = {}) => {
    const nextSettings = {
      ...(carouselAutomationSettings || {}),
      ...updates
    };

    setSavingCarouselSettings(true);
    try {
      const response = await adminAPI.updateCarouselAutomationSettings({
        enabled: !!nextSettings.enabled,
        updateAfterRuns: nextSettings.updateAfterRuns || [2, 4],
        maxAutoItems: nextSettings.maxAutoItems || 4,
        heroMediaPoster: nextSettings.heroMediaPoster !== false,
        minDurationSeconds: Number.isFinite(nextSettings.minDurationSeconds) ? nextSettings.minDurationSeconds : 240
      });
      setCarouselAutomationSettings(response.data?.settings || nextSettings);
      setAlert({ type: 'success', message: 'Carousel automation settings updated.' });
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to save automation settings: ' + handleAPIError(error) });
    } finally {
      setSavingCarouselSettings(false);
    }
  };

  const refreshAutoCarouselManually = async () => {
    try {
      const response = await adminAPI.refreshAutoCarousel(featuredAudience);
      setAlert({
        type: 'success',
        message: response.data?.result?.skipped
          ? `Auto carousel refresh skipped: ${response.data?.result?.reason || 'no changes'}`
          : 'Auto carousel refresh completed.'
      });
      fetchFeaturedData();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to refresh auto carousel: ' + handleAPIError(error) });
    }
  };

  const toggleChannelAutoEligibility = async (channel) => {
    const channelKey = channel.id || channel.name;
    setUpdatingCarouselEligibility(channelKey);
    try {
      await adminAPI.updateChannelCategories(
        channel.name,
        channel.categories || ['general'],
        channel.primaryCategory || (channel.categories?.[0] || 'general'),
        channel.reactorType || null,
        !channel.autoCarouselEligible
      );
      setAlert({
        type: 'success',
        message: `${channel.name} ${channel.autoCarouselEligible ? 'removed from' : 'added to'} auto carousel eligibility.`
      });
      await Promise.all([loadData(), fetchFeaturedData()]);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to update channel eligibility: ' + handleAPIError(error) });
    } finally {
      setUpdatingCarouselEligibility('');
    }
  };

  const moveCarouselItem = (index, direction) => {
    setCarouselItems((prev) => {
      const next = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next.map((item, itemIndex) => ({ ...item, order: itemIndex }));
    });
  };

  const updateCarouselOrderField = (id, value) => {
    setCarouselItems((prev) => prev.map((item) => (
      (item.id || item._id) === id
        ? { ...item, order: Number.isFinite(Number(value)) ? Number(value) : item.order }
        : item
    )));
  };

  const saveCarouselOrder = async () => {
    setSavingCarouselOrder(true);
    try {
      const payload = [...carouselItems]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((item, index) => ({
          id: item.id || item._id,
          order: index
        }));

      await adminFeaturedAPI.reorderFeatured(payload);
      setAlert({ type: 'success', message: 'Carousel order updated.' });
      fetchFeaturedData();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to save carousel order: ' + handleAPIError(error) });
    } finally {
      setSavingCarouselOrder(false);
    }
  };

  // Handle removing items from carousel or featured channels
  const handleRemoveFromFeatured = async (item, type) => {
    try {
      await adminFeaturedAPI.deleteFeatured(item.id || item._id);
      setAlert({ type: 'success', message: `Removed from ${type}!` });
      fetchFeaturedData();
    } catch (error) {
      setAlert({ type: 'error', message: `Failed to remove: ` + handleAPIError(error) });
    }
  };

  // Extract video ID from YouTube URL
  const extractVideoIdFromURL = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
      /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Add video to carousel
  const handleAddVideoToCarousel = async (video) => {
    try {
      const videoId = video.videoId || video.id || video._id;
      const channelId = video.channelId || video.channel?.id || video.channel?.channelId;
      const channelName = video.channelName || video.channel?.name || video.channel?.channelName || 'Unknown Channel';
      const videoTitle = video.title || video.videoTitle || video.snippet?.title;
      const thumbnail = video.thumbnail || video.snippet?.thumbnails?.medium?.url || video.snippet?.thumbnails?.default?.url;

      if (!videoId) {
        setAlert({ type: 'error', message: 'Video ID not found in video data' });
        return;
      }

      const carouselData = {
        type: 'video',
        audience: featuredAudience,
        title: videoTitle,
        description: `Reaction video from ${channelName}`,
        channelName: channelName,
        channelId: channelId,
        videoId: videoId,
        videoTitle: videoTitle,
        videoThumbnail: thumbnail,
        youtubeUrl: `https://youtube.com/watch?v=${videoId}`,
        order: 0
      };

      await adminFeaturedAPI.addCarouselItem(carouselData);
      setAlert({ type: 'success', message: `Video "${videoTitle}" added to carousel!` });

      // Clear results and close modal
      setVideoResults([]);
      setChannelResults([]);
      setUrlInput('');
      setVideoSearchTerm('');
      setChannelSearchTerm('');
      setSearchModalOpen(false);
      fetchFeaturedData();
    } catch (error) {
      console.error('Add video error:', error);
      setAlert({ type: 'error', message: 'Failed to add video: ' + handleAPIError(error) });
    }
  };

  // Add channel to carousel
  const handleAddChannelToCarousel = async (channel) => {
    try {
      const channelId = channel.channelId || channel.id || channel._id;
      const channelName = channel.name || channel.title || channel.channelName;
      const thumbnail = channel.thumbnail || channel.snippet?.thumbnails?.medium?.url || channel.snippet?.thumbnails?.default?.url;

      if (!channelId) {
        setAlert({ type: 'error', message: 'Channel ID not found in channel data' });
        return;
      }

      const carouselData = {
        type: 'channel',
        audience: featuredAudience,
        title: channelName,
        description: 'Featured channel for marketing',
        channelName: channelName,
        channelId: channelId,
        channelThumbnail: thumbnail,
        youtubeUrl: `https://youtube.com/channel/${channelId}`,
        order: 0
      };

      await adminFeaturedAPI.addCarouselItem(carouselData);
      setAlert({ type: 'success', message: `Channel "${channelName}" added to carousel!` });

      // Clear results and close modal
      setVideoResults([]);
      setChannelResults([]);
      setUrlInput('');
      setVideoSearchTerm('');
      setChannelSearchTerm('');
      setSearchModalOpen(false);
      fetchFeaturedData();
    } catch (error) {
      console.error('Add channel error:', error);
      setAlert({ type: 'error', message: 'Failed to add channel: ' + handleAPIError(error) });
    }
  };

  // Search videos by URL
  const searchVideosByURL = async () => {
    if (!urlInput.trim()) return;
    setSearching(true);
    try {
      const videoId = extractVideoIdFromURL(urlInput.trim());
      if (!videoId) {
        setAlert({ type: 'error', message: 'Invalid YouTube URL format' });
        setSearching(false);
        return;
      }

      try {
        const directResponse = await adminAPI.searchVideosDirectly({ videoId: videoId });
        if (directResponse && directResponse.data && directResponse.data.data && directResponse.data.data.length > 0) {
          setVideoResults(directResponse.data.data);
          setAlert({ type: 'success', message: `Found ${directResponse.data.data.length} video(s) matching ID: ${videoId}` });
          setSearching(false);
          return;
        }
      } catch (directError) {
        console.log('Direct search failed, trying featured API:', directError.message);
      }

      const response = await adminFeaturedAPI.searchContent(videoId, 'video');
      if (response && response.data && response.data.results && response.data.results.length > 0) {
        setVideoResults(response.data.results);
        setAlert({ type: 'success', message: `Found ${response.data.results.length} video(s) matching ID: ${videoId}` });
      } else {
        setVideoResults([]);
        setAlert({ type: 'error', message: `Video ID "${videoId}" not found in database. Make sure the video has been ingested first.` });
      }
    } catch (error) {
      console.error('Search error:', error);
      setAlert({ type: 'error', message: 'Failed to search: ' + (error.message || 'Unknown error') });
      setVideoResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Fetch forum threads
  const fetchThreads = useCallback(async () => {
    if (activeTab !== 'forum') return;
    setThreadsLoading(true);
    try {
      const response = await forumAPI.getThreads({
        page: 1,
        limit: 100,
        sort: 'latest'
      });
      // response.data already contains { success, threads, pagination }
      setThreads(response.threads || []);
    } catch (error) {
      console.error('Error fetching threads:', error);
      setAlert({ type: 'error', message: 'Failed to load threads: ' + handleAPIError(error) });
      setThreads([]); // Set empty array on error
    } finally {
      setThreadsLoading(false);
    }
  }, [activeTab]);

  // Thread moderation actions
  const toggleThreadPin = async (threadId, currentStatus) => {
    try {
      await forumAPI.updateThread(threadId, { isPinned: !currentStatus });
      setAlert({ type: 'success', message: `Thread ${!currentStatus ? 'pinned' : 'unpinned'} successfully` });
      fetchThreads();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to update thread: ' + handleAPIError(error) });
    }
  };

  const toggleThreadLock = async (threadId, currentStatus) => {
    try {
      await forumAPI.updateThread(threadId, { isLocked: !currentStatus });
      setAlert({ type: 'success', message: `Thread ${!currentStatus ? 'locked' : 'unlocked'} successfully` });
      fetchThreads();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to update thread: ' + handleAPIError(error) });
    }
  };

  const toggleThreadVisibility = async (threadId, currentStatus) => {
    try {
      await forumAPI.updateThread(threadId, { isHidden: !currentStatus });
      setAlert({ type: 'success', message: `Thread ${!currentStatus ? 'hidden' : 'shown'} successfully` });
      fetchThreads();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to update thread: ' + handleAPIError(error) });
    }
  };

  const deleteThread = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this thread? This action cannot be undone.')) {
      return;
    }
    try {
      await forumAPI.deleteThread(threadId);
      setAlert({ type: 'success', message: 'Thread deleted successfully' });
      fetchThreads();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to delete thread: ' + handleAPIError(error) });
    }
  };

  // Load threads when forum tab is active
  useEffect(() => {
    if (activeTab === 'forum') {
      fetchThreads();
    }
  }, [activeTab, fetchThreads]);


  const StatCard = ({ icon: Icon, label, value, change, trend }) => (
    <div style={{
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '24px',
      transition: 'all 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = colors.accent;
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = colors.border;
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <Icon size={24} style={{ color: colors.accent }} />
        {change && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            color: trend === 'up' ? colors.success : colors.error
          }}>
            <TrendingUp size={16} style={{ transform: trend === 'down' ? 'rotate(180deg)' : 'none' }} />
            {change}%
          </div>
        )}
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '4px' }}>
        {value || '0'}
      </div>
      <div style={{ fontSize: '14px', color: colors.textSecondary }}>
        {label}
      </div>
    </div>
  );

  const Sidebar = () => {
    const menuItems = ADMIN_MENU_ITEMS;
    const activeItem = menuItems.find(item => item.id === activeTab) || menuItems[0];

    if (isMobile) {
      return (
        <div className="reactorhub-admin-mobile-nav" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          backgroundColor: colors.bgSecondary,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 100,
          padding: '10px 12px',
          paddingTop: 'calc(10px + env(safe-area-inset-top))',
          boxSizing: 'border-box',
          boxShadow: '0 8px 24px rgba(0,0,0,0.24)'
        }}>
          <div style={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            backgroundColor: `${colors.accent}22`,
            color: colors.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <activeItem.icon size={19} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '3px' }}>
              Admin
            </div>
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              aria-label="Admin section"
              style={{
                width: '100%',
                minHeight: '34px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '14px',
                fontWeight: 700,
                padding: '6px 10px',
                outline: 'none'
              }}
            >
              {menuItems.map(item => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
            style={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgTertiary,
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      );
    }

    return (
      <div style={{
        width: '260px',
        height: '100vh',
        backgroundColor: colors.bgSecondary,
        borderRight: `1px solid ${colors.border}`,
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0'
      }}>
        {/* Logo */}
        <div style={{ padding: '0 24px', marginBottom: '32px' }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            ReactorHub
          </div>
          <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
            Admin Dashboard
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '0 12px', minHeight: 0, overflowY: 'auto' }}>
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <div
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '4px',
                  cursor: 'pointer',
                  backgroundColor: isActive ? colors.accent : 'transparent',
                  color: isActive ? '#FFFFFF' : colors.text,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.backgroundColor = colors.bgTertiary;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Icon size={20} />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>{item.label}</span>
              </div>
            );
          })}
        </nav>

        {/* AI Assistant Chat */}
        <AdminAIChat
          colors={colors}
          isExpanded={aiChatExpanded}
          onToggle={() => setAiChatExpanded(!aiChatExpanded)}
        />

        {/* Theme Toggle */}
        <div style={{ padding: '0 24px', marginTop: '16px' }}>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgTertiary,
              color: colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>
      </div>
    );
  };

  // ── Dashboard helper components ──
  const DashboardCard = ({ children, style = {} }) => (
    <div style={{
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '24px',
      ...style,
    }}>{children}</div>
  );

  const CardTitle = ({ children, right }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, margin: 0 }}>{children}</h3>
      {right}
    </div>
  );

  const MiniBar = ({ items, colorMap }) => {
    const max = Math.max(...items.map(i => i.value), 1);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '110px', fontSize: '13px', color: colors.text, fontWeight: '500', textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.label}
            </div>
            <div style={{ flex: 1, height: '28px', backgroundColor: colors.bgTertiary, borderRadius: '6px', overflow: 'hidden', position: 'relative' }}>
              <div style={{
                height: '100%',
                width: `${Math.max((item.value / max) * 100, 2)}%`,
                backgroundColor: colorMap?.[item.label] || colors.accent,
                borderRadius: '6px',
                transition: 'width 0.6s ease',
              }} />
            </div>
            <div style={{ minWidth: '60px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: colors.text }}>
              {item.value.toLocaleString()}
            </div>
            <div style={{ minWidth: '36px', textAlign: 'right', fontSize: '12px', color: colors.textSecondary }}>
              {item.pct}%
            </div>
          </div>
        ))}
      </div>
    );
  };

  const DonutChart = ({ items, size = 140 }) => {
    const total = items.reduce((s, i) => s + i.value, 0) || 1;
    let cumulative = 0;
    const segments = items.map(item => {
      const start = cumulative;
      const pct = (item.value / total) * 100;
      cumulative += pct;
      return { ...item, start, pct };
    });
    const gradientParts = segments.map(s => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <div style={{
          width: size, height: size, borderRadius: '50%',
          background: `conic-gradient(${gradientParts})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <div style={{ width: size - 40, height: size - 40, borderRadius: '50%', backgroundColor: colors.bgSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: colors.text }}>{total}</div>
            <div style={{ fontSize: '11px', color: colors.textSecondary }}>Total</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {segments.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <div style={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: s.color, flexShrink: 0 }} />
              <span style={{ color: colors.text, fontWeight: '500', textTransform: 'capitalize' }}>{s.label}</span>
              <span style={{ color: colors.textSecondary, marginLeft: 'auto' }}>{s.pct.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const GrowthBadge = ({ value }) => {
    if (value === undefined || value === null) return null;
    const isUp = value >= 0;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        fontSize: '13px', fontWeight: '600', padding: '2px 8px', borderRadius: '6px',
        backgroundColor: isUp ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
        color: isUp ? colors.success : colors.error,
      }}>
        <TrendingUp size={14} style={{ transform: isUp ? 'none' : 'rotate(180deg)' }} />
        {Math.abs(value)}%
      </span>
    );
  };

  const DashboardView = () => {
    const ov = dashboardOverview;
    const kpi = ov?.kpis || {};
    const dist = ov?.distributions || {};

    // Build distribution arrays
    const catItems = Object.entries(dist.categories || {}).map(([label, value]) => ({
      label, value, pct: Math.round((value / (Object.values(dist.categories || {}).reduce((a, b) => a + b, 0) || 1)) * 100),
    })).sort((a, b) => b.value - a.value);

    const mediaItems = Object.entries(dist.mediaTypes || {}).map(([label, value]) => ({
      label, value, pct: Math.round((value / (Object.values(dist.mediaTypes || {}).reduce((a, b) => a + b, 0) || 1)) * 100),
    })).sort((a, b) => b.value - a.value);

    const reactorItems = Object.entries(dist.reactorTypes || {}).map(([label, value]) => ({
      label, value,
      color: CH_TYPE_COLORS[label] || '#6B7280',
    }));

    const catColorMap = { movies: '#8B5CF6', 'tv-shows': '#3B82F6', anime: '#F97316', 'music-performance': '#EC4899', 'korean-drama': '#EF4444', general: '#6B7280' };
    const mediaColorMap = { movie: '#8B5CF6', series: '#3B82F6', anime: '#F97316', music: '#EC4899', dorama: '#EF4444' };

    if (dashboardLoading && !ov) {
      return (
        <div style={{ padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center', color: colors.textSecondary }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
            <div>Loading dashboard data...</div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      );
    }

    return (
      <div style={{ padding: '32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: colors.text, marginBottom: '4px' }}>
              Dashboard Overview
            </h1>
            <p style={{ fontSize: '14px', color: colors.textSecondary, margin: 0 }}>
              Platform analytics &middot; Last 30 days
            </p>
          </div>
          <button onClick={fetchDashboardOverview} style={{
            padding: '8px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`,
            backgroundColor: colors.bgSecondary, color: colors.text, cursor: 'pointer',
            fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <RefreshCw size={14} style={dashboardLoading ? { animation: 'spin 1s linear infinite' } : {}} />
            Refresh
          </button>
        </div>

        {/* ── Row 1: KPI Cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard icon={Users} label="Total Users" value={kpi.totalUsers || 0} change={kpi.userGrowth} trend={kpi.userGrowth >= 0 ? 'up' : 'down'} />
          <StatCard icon={Radio} label="Channels (DB)" value={kpi.dbChannels || 0} change={kpi.channelGrowth} trend={kpi.channelGrowth >= 0 ? 'up' : 'down'} />
          <StatCard icon={Film} label="Media Library" value={kpi.totalMedia || 0} />
          <StatCard icon={Video} label="Videos Indexed" value={kpi.totalVideos || 0} />
          <StatCard icon={ThumbsUp} label="Total Votes" value={kpi.totalVotes || 0} change={kpi.voteGrowth} trend={kpi.voteGrowth >= 0 ? 'up' : 'down'} />
        </div>

        {/* ── Row 2: Secondary KPIs ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'Active Polls', value: kpi.activePolls || 0, total: kpi.totalPolls, icon: Activity, color: colors.warning },
            { label: 'Forum Threads', value: kpi.totalForumThreads || 0, sub: `${kpi.newThreadsThisMonth || 0} new`, icon: MessageSquare, color: colors.info },
            { label: 'Forum Posts', value: kpi.totalForumPosts || 0, icon: MessageSquare, color: '#8B5CF6' },
            { label: 'Peak Moments', value: kpi.approvedPeakMoments || 0, total: kpi.totalPeakMoments, icon: Flame, color: '#F97316' },
            { label: 'Creators', value: kpi.creatorCount || 0, icon: Award, color: colors.success },
            { label: 'New Videos (30d)', value: kpi.newVideosThisMonth || 0, icon: Upload, color: '#EC4899' },
          ].map((item, i) => (
            <DashboardCard key={i} style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <item.icon size={16} style={{ color: item.color }} />
                <span style={{ fontSize: '12px', color: colors.textSecondary }}>{item.label}</span>
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: colors.text }}>
                {(item.value || 0).toLocaleString()}
              </div>
              {item.total !== undefined && (
                <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                  of {item.total} total
                </div>
              )}
              {item.sub && (
                <div style={{ fontSize: '11px', color: colors.success, marginTop: '2px' }}>{item.sub}</div>
              )}
            </DashboardCard>
          ))}
        </div>

        <DashboardCard style={{ marginBottom: '24px' }}>
          <CardTitle>Signup Snapshot</CardTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
            {[
              { label: 'All Time', value: kpi.totalUsers || 0, color: colors.accent },
              { label: 'Last 7 Days', value: kpi.newUsers7d || 0, color: colors.success },
              { label: 'Active 7 Days', value: kpi.activeUsers7d || 0, color: '#22C55E' },
              { label: 'Last 30 Days', value: kpi.newUsersThisMonth || 0, color: colors.info },
              { label: 'Last 6 Months', value: kpi.newUsers6m || 0, color: colors.warning },
              { label: 'Last 12 Months', value: kpi.newUsers1y || 0, color: '#EC4899' },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: '16px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgSecondary,
                  borderTop: `3px solid ${item.color}`
                }}
              >
                <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                  Signups {item.label}
                </div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: colors.text }}>
                  {(item.value || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </DashboardCard>

        {/* ── Row 3: Category Distribution + Media Type Distribution ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Channel Categories Bar Chart */}
          <DashboardCard>
            <CardTitle>Channels by Category</CardTitle>
            {catItems.length > 0 ? (
              <MiniBar items={catItems} colorMap={catColorMap} />
            ) : (
              <div style={{ color: colors.textSecondary, fontSize: '14px', textAlign: 'center', padding: '40px 0' }}>No category data yet</div>
            )}
          </DashboardCard>

          {/* Media Type Bar Chart */}
          <DashboardCard>
            <CardTitle>Media by Type</CardTitle>
            {mediaItems.length > 0 ? (
              <MiniBar items={mediaItems} colorMap={mediaColorMap} />
            ) : (
              <div style={{ color: colors.textSecondary, fontSize: '14px', textAlign: 'center', padding: '40px 0' }}>No media data yet</div>
            )}
          </DashboardCard>
        </div>

        {/* ── Row 4: Reactor Types Donut + Quota + Channel Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Reactor Type Distribution */}
          <DashboardCard>
            <CardTitle>Reactor Types</CardTitle>
            {reactorItems.length > 0 ? (
              <DonutChart items={reactorItems} />
            ) : (
              <div style={{ color: colors.textSecondary, fontSize: '14px', textAlign: 'center', padding: '40px 0' }}>No data</div>
            )}
          </DashboardCard>

          {/* YouTube Quota Usage */}
          <DashboardCard>
            <CardTitle>YouTube API Quota</CardTitle>
            {(() => {
              const q = ov?.quota || {};
              const pct = q.percentage || 0;
              const barColor = pct > 80 ? colors.error : pct > 50 ? colors.warning : colors.success;
              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '28px', fontWeight: '700', color: colors.text }}>{(q.used || 0).toLocaleString()}</span>
                    <span style={{ fontSize: '14px', color: colors.textSecondary, alignSelf: 'flex-end' }}>/ {(q.limit || 1000).toLocaleString()}</span>
                  </div>
                  <div style={{ height: '10px', backgroundColor: colors.bgTertiary, borderRadius: '5px', overflow: 'hidden', marginBottom: '12px' }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: barColor, borderRadius: '5px', transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ fontSize: '13px', color: barColor, fontWeight: '500' }}>
                    {pct.toFixed(1)}% used today
                  </div>
                </div>
              );
            })()}
          </DashboardCard>

          {/* Channel Quick Stats */}
          <DashboardCard>
            <CardTitle>Channel Overview</CardTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Total (JSON)', value: ov?.channelStats?.total || data.stats?.total || 0, color: colors.accent },
                { label: 'Whitelisted', value: ov?.channelStats?.whitelisted || data.stats?.whitelisted || 0, color: colors.success },
                { label: 'Blacklisted', value: ov?.channelStats?.blacklisted || data.stats?.blacklisted || 0, color: colors.error },
                { label: 'With YouTube IDs', value: ov?.channelStats?.withIds || data.stats?.withIds || 0, color: colors.info },
                { label: 'In Database', value: ov?.channelStats?.inDatabase || 0, color: colors.warning },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                    <span style={{ fontSize: '13px', color: colors.textSecondary }}>{item.label}</span>
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: colors.text }}>{item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </DashboardCard>
        </div>

        {/* ── Row 5: Top Channels Leaderboard + Top Voted Media + Recent Users ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Top Channels */}
          <DashboardCard>
            <CardTitle>Top Channels by Subscribers</CardTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {(ov?.topChannels || []).slice(0, 8).map((ch, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px',
                  borderRadius: '8px', backgroundColor: i < 3 ? `${colors.accent}10` : 'transparent',
                }}>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : colors.bgTertiary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700', color: i < 3 ? '#000' : colors.textSecondary,
                  }}>{i + 1}</div>
                  {ch.avatarUrl ? (
                    <img src={ch.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: colors.bgTertiary }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ch.channelName}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                      {fmtSubs(ch.subscriberCount)} subs
                    </div>
                  </div>
                  <span style={{
                    fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                    backgroundColor: CH_CAT_COLORS[ch.primaryCategory] || colors.bgTertiary,
                    color: '#fff', textTransform: 'capitalize', whiteSpace: 'nowrap',
                  }}>{ch.primaryCategory || 'general'}</span>
                </div>
              ))}
              {(!ov?.topChannels || ov.topChannels.length === 0) && (
                <div style={{ color: colors.textSecondary, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No channels in database yet</div>
              )}
            </div>
          </DashboardCard>

          {/* Top Voted Media */}
          <DashboardCard>
            <CardTitle>Most Voted Media</CardTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(ov?.topMedia || []).map((media, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', backgroundColor: colors.bgTertiary }}>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : colors.border,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700', color: i < 3 ? '#000' : colors.textSecondary,
                  }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {media.title}
                    </div>
                    <span style={{
                      fontSize: '10px', padding: '1px 5px', borderRadius: '3px',
                      backgroundColor: mediaColorMap[media.type] || colors.bgTertiary,
                      color: '#fff', textTransform: 'capitalize',
                    }}>{media.type}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: colors.accent, fontSize: '13px', fontWeight: '600' }}>
                    <ThumbsUp size={13} />
                    {media.votes}
                  </div>
                </div>
              ))}
              {(!ov?.topMedia || ov.topMedia.length === 0) && (
                <div style={{ color: colors.textSecondary, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No votes recorded yet</div>
              )}
            </div>
          </DashboardCard>

          {/* Recent Users */}
          <DashboardCard>
            <CardTitle right={<GrowthBadge value={kpi.userGrowth} />}>New Users (7 days)</CardTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(ov?.recentUsers || []).map((user, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', backgroundColor: colors.bgTertiary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: '600', color: colors.accent,
                    overflow: 'hidden',
                  }}>
                    {user.avatar ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (user.username?.[0] || '?').toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>{user.username}</div>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                    backgroundColor: user.role === 'admin' ? colors.error : user.role === 'creator' ? colors.warning : colors.bgTertiary,
                    color: user.role === 'admin' || user.role === 'creator' ? '#fff' : colors.textSecondary,
                    textTransform: 'capitalize',
                  }}>{user.role || 'user'}</span>
                </div>
              ))}
              {(!ov?.recentUsers || ov.recentUsers.length === 0) && (
                <div style={{ color: colors.textSecondary, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No recent signups</div>
              )}
            </div>
          </DashboardCard>
        </div>

        {/* ── Row 6: Recent Forum Activity ── */}
        <DashboardCard>
          <CardTitle>Recent Forum Threads</CardTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
            {(ov?.recentThreads || []).map((thread, i) => {
              const catColor = { discussion: colors.info, question: colors.warning, review: colors.accent, reaction_request: '#EC4899', news: colors.success, meme: '#F97316' };
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: colors.bgTertiary }}>
                  <MessageSquare size={16} style={{ color: catColor[thread.category] || colors.textSecondary, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {thread.title}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                      by {thread.authorUsername} &middot; {new Date(thread.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                    backgroundColor: catColor[thread.category] || colors.bgTertiary,
                    color: '#fff', textTransform: 'capitalize', whiteSpace: 'nowrap',
                  }}>{(thread.category || '').replace('_', ' ')}</span>
                </div>
              );
            })}
            {(!ov?.recentThreads || ov.recentThreads.length === 0) && (
              <div style={{ color: colors.textSecondary, fontSize: '13px', padding: '12px' }}>No recent forum activity</div>
            )}
          </div>
        </DashboardCard>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  };


  const SUGGESTION_CONFIGS = [
    { key: 'channelOfWeek',   label: 'Channel of the Week', position: 'Channel of the Week', color: '#FFD700', icon: Award },
    { key: 'risingStar',      label: 'Rising Star',          position: 'Rising Star',          color: '#10B981', icon: TrendingUp },
    { key: 'mostTalkedAbout', label: 'Most Talked About',    position: 'Most Talked About',    color: '#F97316', icon: Flame },
    { key: 'staffPick',       label: 'Staff Pick',           position: 'Staff Pick',           color: '#EC4899', icon: Sparkles },
  ];

  const SmartSuggestionsPanel = () => {
    const [open, setOpen] = useState(true);
    if (suggestionsLoading) return (
      <div style={{ background: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '24px', marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '12px', color: colors.textSecondary, fontSize: '14px' }}>
        <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', color: '#8B5CF6' }} />
        Analyzing engagement data...
      </div>
    );
    if (!suggestions?.suggestions) return null;

    const fmtNum = n => n >= 1000 ? (n / 1000).toFixed(1) + 'K' : n;
    const allEmpty = SUGGESTION_CONFIGS.every(cfg => (suggestions.suggestions[cfg.key] || []).length === 0);

    return (
      <div style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(236,72,153,0.06))', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '12px', marginBottom: '32px', overflow: 'hidden' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', background: 'none', border: 'none', cursor: 'pointer', color: colors.text }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Sparkles size={20} style={{ color: '#8B5CF6' }} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>Smart Suggestions</div>
              <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>Data-driven candidates based on clicks, favorites &amp; votes this week</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: colors.textTertiary, display: 'flex', alignItems: 'center', gap: '6px' }}>
            {open ? 'Collapse' : 'Expand'}
            <ChevronRight size={14} style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </div>
        </button>

        {open && (
          <div style={{ padding: '0 24px 24px' }}>
            {allEmpty ? (
              <div style={{ textAlign: 'center', padding: '32px', color: colors.textTertiary, fontSize: '14px' }}>
                Not enough engagement data yet — suggestions appear as users interact with the platform.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {SUGGESTION_CONFIGS.map(cfg => {
                  const list = suggestions.suggestions[cfg.key] || [];
                  const Icon = cfg.icon;
                  if (list.length === 0) return null;
                  return (
                    <div key={cfg.key} style={{ background: colors.bgSecondary, borderRadius: '10px', padding: '16px', border: `1px solid ${colors.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <Icon size={16} style={{ color: cfg.color }} />
                        <span style={{ fontSize: '13px', fontWeight: '600', color: cfg.color }}>{cfg.label}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {list.slice(0, 3).map((ch, i) => (
                          <div key={ch.channelId} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '8px', background: i === 0 ? `${cfg.color}10` : 'transparent', border: i === 0 ? `1px solid ${cfg.color}30` : '1px solid transparent' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: i === 0 ? cfg.color : colors.textTertiary, minWidth: '16px' }}>#{i + 1}</span>
                            {ch.thumbnail ? (
                              <img src={ch.thumbnail} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${cfg.color}30`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: cfg.color }}>
                                {(ch.channelName?.[0] || '?').toUpperCase()}
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ch.channelName}</div>
                              <div style={{ fontSize: '11px', color: colors.textTertiary, display: 'flex', gap: '8px', marginTop: '2px' }}>
                                {ch.clicks    != null && <span>{fmtNum(ch.clicks)} clicks</span>}
                                {ch.favorites != null && ch.favorites > 0 && <span>{fmtNum(ch.favorites)} favs</span>}
                                {ch.growth    != null && <span style={{ color: ch.growth >= 0 ? '#10B981' : '#EF4444' }}>{ch.growth >= 0 ? '+' : ''}{ch.growth}%</span>}
                                {ch.score     != null && <span style={{ color: cfg.color, fontWeight: '600' }}>{ch.score}pts</span>}
                              </div>
                            </div>
                            <button
                              onClick={() => setFeaturedOfWeek(cfg.position, ch.channelId)}
                              style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: cfg.color, color: '#FFF', fontSize: '11px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                              Promote
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reaction of the Week suggestions — based on user-chosen reactions from content pages */}
            {suggestions.reactionOfWeek?.length > 0 && (
              <div style={{ marginTop: '16px', background: colors.bgSecondary, borderRadius: '10px', padding: '16px', border: `1px solid ${colors.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Play size={16} style={{ color: '#F59E0B' }} />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#F59E0B' }}>Reaction of the Week Candidates</span>
                  </div>
                  <span style={{ fontSize: '11px', color: colors.textTertiary }}>Most chosen by users from content pages this week</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {suggestions.reactionOfWeek.slice(0, 3).map((r, i) => (
                    <a key={r.videoId} href={r.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <div style={{ borderRadius: '8px', overflow: 'hidden', border: `1px solid ${colors.border}`, background: colors.bgTertiary, cursor: 'pointer', transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#F59E0B'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
                      >
                        <div style={{ position: 'relative' }}>
                          <img src={r.thumbnail} alt="" style={{ width: '100%', height: '72px', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
                          <div style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.7)', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', fontWeight: '700', color: '#F59E0B' }}>#{i + 1}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          {r.contentTitle && <div style={{ fontSize: '10px', color: '#F59E0B', fontWeight: '600', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{r.contentTitle}</div>}
                          <div style={{ fontSize: '11px', color: colors.text, fontWeight: '500' }}>{r.clicks} user clicks</div>
                          {r.contentType && <div style={{ fontSize: '10px', color: colors.textTertiary, marginTop: '2px', textTransform: 'capitalize' }}>{r.contentType}</div>}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const FeaturedView = () => {
    const approvedMedia = mediaContent || [];
    const availableFeaturedChannels = featuredAudience === 'music'
      ? (featuredChannels || data.channels || []).filter((ch) => ch.primaryCategory === 'music-performance' || ch.categories?.includes('music-performance'))
      : (featuredChannels || data.channels || []);
    const movieMediaOptions = approvedMedia.filter(media => media.type === 'movie');
    const tvMediaOptions = approvedMedia.filter(media => media.type === 'series');
    const musicMediaOptions = approvedMedia.filter(media => media.type === 'music');
    const selectedFeaturedMovie = findMediaBySelection(featuredMovieId);
    const selectedFeaturedTvShow = findMediaBySelection(featuredTvShowId);
    const selectedFeaturedMusic = findMediaBySelection(featuredMusicId);
    const selectedMostWanted = mostWantedIds.map(id => findMediaBySelection(id));
    const formatMediaOptionLabel = (media) => `${media.title}${media.releaseYear ? ` (${media.releaseYear})` : ''} - ${(media.type || '').replace(/^./, c => c.toUpperCase())}`;
    const eligibleCarouselChannels = availableFeaturedChannels
      .filter((channel) => (channel.name || '').toLowerCase().includes(carouselChannelSearch.toLowerCase()))
      .sort((a, b) => Number(!!b.autoCarouselEligible) - Number(!!a.autoCarouselEligible) || (a.name || '').localeCompare(b.name || ''));
    const autoCarouselCount = (featuredChannels || data.channels || []).filter((channel) => channel.autoCarouselEligible).length;
    const orderedCarouselItems = [...carouselItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const manualCarouselItems = orderedCarouselItems.filter((item) => item.addedBy !== 'auto-carousel');
    const autoCarouselItems = orderedCarouselItems.filter((item) => item.addedBy === 'auto-carousel');
    const selectedRankingsCount = [channelOfWeek, risingStar, mostTalked, staffPick].filter(Boolean).length;
    const selectedMediaCount = [featuredMovieId, featuredTvShowId, featuredMusicId, ...mostWantedIds].filter(Boolean).length;
    const selectedReactionVideosCount = [reactionVideo1, reactionVideo2, reactionVideo3].filter((value) => value && value.trim()).length;
    const sectionCardStyle = {
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px'
    };
    const sectionTitleStyle = { fontSize: '22px', fontWeight: '700', color: colors.text, margin: 0 };
    const quickLinks = [
      { id: 'explore-rankings-section', label: 'Channel Rankings' },
      { id: 'explore-media-section', label: 'Featured Media' },
      { id: 'explore-reaction-section', label: 'Reaction Banner' },
      { id: 'explore-auto-carousel-section', label: 'Auto Carousel' },
      { id: 'explore-carousel-section', label: 'Carousel Items' },
      { id: 'explore-preview-section', label: 'Homepage Preview' }
    ];

    return (
    <div style={{ padding: '32px' }}>
      <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '8px' }}>
        Explore Rankings Console
      </h1>
      <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '32px' }}>
        Professional control surface for Explore rankings, homepage carousel placements, featured media, and reaction banner programming.
      </p>

      <div style={{ ...sectionCardStyle, padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <div>
            <div style={{ fontSize: '12px', color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Workspace</div>
            <div style={{ display: 'inline-flex', gap: '6px', padding: '4px', borderRadius: '12px', border: `1px solid ${colors.border}`, backgroundColor: colors.bgTertiary }}>
              {[
                { id: 'general', label: 'General Control' },
                { id: 'music', label: 'Music Control' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setFeaturedAudience(item.id)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '9px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '700',
                    backgroundColor: featuredAudience === item.id ? colors.accent : 'transparent',
                    color: featuredAudience === item.id ? '#FFFFFF' : colors.textSecondary
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', flex: '1 1 560px' }}>
            {[
              { label: 'Rankings Set', value: `${selectedRankingsCount}/4`, tone: colors.accent },
              { label: 'Media Slots Filled', value: `${selectedMediaCount}/6`, tone: colors.success },
              { label: 'Reaction Videos Live', value: `${selectedReactionVideosCount}/3`, tone: colors.warning },
              { label: 'Carousel Items', value: orderedCarouselItems.length, tone: colors.info },
              { label: 'Manual / Auto', value: `${manualCarouselItems.length} / ${autoCarouselItems.length}`, tone: '#EC4899' }
            ].map((card) => (
              <div key={card.label} style={{ backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{card.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: card.tone }}>{card.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '16px' }}>
          <div style={{ fontSize: '12px', color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Sections</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {quickLinks.map((section) => {
              const isActiveSection = exploreSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setExploreSection(section.id)}
                  style={{
                    padding: '9px 14px',
                    borderRadius: '999px',
                    border: `1px solid ${isActiveSection ? colors.accent : colors.border}`,
                    backgroundColor: isActiveSection ? colors.accent : colors.bgTertiary,
                    color: isActiveSection ? '#FFFFFF' : colors.textSecondary,
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease'
                  }}
                >
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div id="explore-rankings-section" style={{ ...sectionCardStyle, display: exploreSection === 'explore-rankings-section' ? 'block' : 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '18px' }}>
          <div>
            <h2 style={sectionTitleStyle}>Channel Ranking Controls</h2>
            <p style={{ color: colors.textSecondary, fontSize: '14px', margin: '8px 0 0' }}>
              Set the editorial leaderboard that drives Explore visibility and the homepage featured reactor preview.
            </p>
          </div>
          <div style={{ padding: '10px 14px', borderRadius: '12px', backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}`, fontSize: '13px', color: colors.textSecondary }}>
            Audience: <span style={{ color: colors.text, fontWeight: '700' }}>{featuredAudience === 'music' ? 'Music' : 'General'}</span>
          </div>
        </div>

        <SmartSuggestionsPanel />

        {featuredAudience === 'general' ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '16px', marginTop: '18px' }}>
        {/* Channel of the Week */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Award size={20} style={{ color: '#FFD700' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
              Channel of the Week (#1)
            </h3>
          </div>
          {channelOfWeek && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px',
              backgroundColor: colors.bgTertiary,
              borderRadius: '6px',
              marginBottom: '8px'
            }}>
              <Check size={16} style={{ color: colors.success }} />
              <span style={{ fontSize: '13px', color: colors.success }}>
                Selected: {availableFeaturedChannels.find(ch => ch.id === channelOfWeek)?.name}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={channelOfWeek}
              onChange={(e) => setChannelOfWeek(e.target.value)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '13px'
              }}>
              <option value="">Select Channel</option>
              {availableFeaturedChannels.map((ch, idx) => (
                <option key={idx} value={ch.id}>{ch.name}</option>
              ))}
            </select>
            <button
              onClick={() => setFeaturedOfWeek('Channel of the Week', channelOfWeek)}
              style={{
                padding: '10px 18px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: colors.accent,
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}>
              Set
            </button>
          </div>
        </div>

        {/* Rising Star */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <TrendingUp size={20} style={{ color: colors.success }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
              Rising Star (#2)
            </h3>
          </div>
          {risingStar && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px',
              backgroundColor: colors.bgTertiary,
              borderRadius: '6px',
              marginBottom: '8px'
            }}>
              <Check size={16} style={{ color: colors.success }} />
              <span style={{ fontSize: '13px', color: colors.success }}>
                Selected: {availableFeaturedChannels.find(ch => ch.id === risingStar)?.name}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={risingStar}
              onChange={(e) => setRisingStar(e.target.value)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '13px'
              }}>
              <option value="">Select Channel</option>
              {availableFeaturedChannels.map((ch, idx) => (
                <option key={idx} value={ch.id}>{ch.name}</option>
              ))}
            </select>
            <button
              onClick={() => setFeaturedOfWeek('Rising Star', risingStar)}
              style={{
                padding: '10px 18px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: colors.success,
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}>
              Set
            </button>
          </div>
        </div>

        {/* Most Talked About */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Flame size={20} style={{ color: colors.warning }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
              Most Talked About (#3)
            </h3>
          </div>
          {mostTalked && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px',
              backgroundColor: colors.bgTertiary,
              borderRadius: '6px',
              marginBottom: '8px'
            }}>
              <Check size={16} style={{ color: colors.success }} />
              <span style={{ fontSize: '13px', color: colors.success }}>
                Selected: {availableFeaturedChannels.find(ch => ch.id === mostTalked)?.name}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={mostTalked}
              onChange={(e) => setMostTalked(e.target.value)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '13px'
              }}>
              <option value="">Select Channel</option>
              {availableFeaturedChannels.map((ch, idx) => (
                <option key={idx} value={ch.id}>{ch.name}</option>
              ))}
            </select>
            <button
              onClick={() => setFeaturedOfWeek('Most Talked About', mostTalked)}
              style={{
                padding: '10px 18px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: colors.warning,
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}>
              Set
            </button>
          </div>
        </div>

        {/* Staff Picks */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Sparkles size={20} style={{ color: '#EC4899' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
              Staff Picks (Unranked)
            </h3>
          </div>
          {staffPick && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px',
              backgroundColor: colors.bgTertiary,
              borderRadius: '6px',
              marginBottom: '8px'
            }}>
              <Check size={16} style={{ color: colors.success }} />
              <span style={{ fontSize: '13px', color: colors.success }}>
                Selected: {availableFeaturedChannels.find(ch => ch.id === staffPick)?.name}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={staffPick}
              onChange={(e) => setStaffPick(e.target.value)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '13px'
              }}>
              <option value="">Select Channel</option>
              {availableFeaturedChannels.map((ch, idx) => (
                <option key={idx} value={ch.id}>{ch.name}</option>
              ))}
            </select>
            <button
              onClick={() => setFeaturedOfWeek('Staff Pick', staffPick)}
              style={{
                padding: '10px 18px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#EC4899',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}>
              Set
            </button>
          </div>
        </div>
      </div>
      ) : (
        <div style={{
          backgroundColor: colors.bgTertiary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '18px',
          marginTop: '18px'
        }}>
          <p style={{ color: colors.textSecondary, fontSize: '14px', margin: 0 }}>
            Music control uses its own carousel and Reaction of the Week lane. The main channel leaderboard slots stay in the general tab.
          </p>
        </div>
      )}
      </div>

      <div id="explore-media-section" style={{ ...sectionCardStyle, display: exploreSection === 'explore-media-section' ? 'block' : 'none' }}>
        <div style={{
          backgroundColor: colors.bgTertiary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <Film size={22} style={{ color: colors.accent }} />
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: colors.text, margin: 0 }}>
              Trending This Week Media
            </h2>
          </div>
          <p style={{ color: colors.textSecondary, fontSize: '14px', margin: 0 }}>
            Control the featured movie, featured TV show, featured music, and Most Wanted items shown in the Explore sidebar.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '16px' }}>
          <div style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Film size={20} style={{ color: colors.accent }} />
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>Featured Movie (#1)</h3>
            </div>
            {selectedFeaturedMovie && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: colors.bgTertiary, borderRadius: '6px', marginBottom: '8px' }}>
                <Check size={16} style={{ color: colors.success }} />
                <span style={{ fontSize: '13px', color: colors.success }}>
                  Selected: {selectedFeaturedMovie.title}{selectedFeaturedMovie.releaseYear ? ` (${selectedFeaturedMovie.releaseYear})` : ''}
                </span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '12px', alignItems: 'center' }}>
              <select
                value={featuredMovieId}
                onChange={(e) => setFeaturedMovieId(e.target.value)}
                style={{ width: '100%', minWidth: 0, padding: '10px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.bgTertiary, color: colors.text, fontSize: '13px' }}>
                <option value="">Select Movie</option>
                {movieMediaOptions.map((media) => (
                  <option key={media.dbId || media.id} value={media.dbId || media.id}>{formatMediaOptionLabel(media)}</option>
                ))}
              </select>
              <button
                onClick={() => saveFeaturedMedia('featuredMovie', featuredMovieId, 'Featured movie')}
                style={{ padding: '10px 18px', borderRadius: '6px', border: 'none', backgroundColor: colors.accent, color: '#FFFFFF', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Set
              </button>
            </div>
          </div>

          <div style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Tv size={20} style={{ color: colors.success }} />
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>Featured TV Show (#1)</h3>
            </div>
            {selectedFeaturedTvShow && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: colors.bgTertiary, borderRadius: '6px', marginBottom: '8px' }}>
                <Check size={16} style={{ color: colors.success }} />
                <span style={{ fontSize: '13px', color: colors.success }}>
                  Selected: {selectedFeaturedTvShow.title}{selectedFeaturedTvShow.releaseYear ? ` (${selectedFeaturedTvShow.releaseYear})` : ''}
                </span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '12px', alignItems: 'center' }}>
              <select
                value={featuredTvShowId}
                onChange={(e) => setFeaturedTvShowId(e.target.value)}
                style={{ width: '100%', minWidth: 0, padding: '10px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.bgTertiary, color: colors.text, fontSize: '13px' }}>
                <option value="">Select TV Show</option>
                {tvMediaOptions.map((media) => (
                  <option key={media.dbId || media.id} value={media.dbId || media.id}>{formatMediaOptionLabel(media)}</option>
                ))}
              </select>
              <button
                onClick={() => saveFeaturedMedia('featuredTvShow', featuredTvShowId, 'Featured TV show')}
                style={{ padding: '10px 18px', borderRadius: '6px', border: 'none', backgroundColor: colors.success, color: '#FFFFFF', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Set
              </button>
            </div>
          </div>

          <div style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Music size={20} style={{ color: colors.warning }} />
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>Featured Music (#1)</h3>
            </div>
            {selectedFeaturedMusic && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: colors.bgTertiary, borderRadius: '6px', marginBottom: '8px' }}>
                <Check size={16} style={{ color: colors.success }} />
                <span style={{ fontSize: '13px', color: colors.success }}>
                  Selected: {selectedFeaturedMusic.title}
                </span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '12px', alignItems: 'center' }}>
              <select
                value={featuredMusicId}
                onChange={(e) => setFeaturedMusicId(e.target.value)}
                style={{ width: '100%', minWidth: 0, padding: '10px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.bgTertiary, color: colors.text, fontSize: '13px' }}>
                <option value="">Select Music</option>
                {musicMediaOptions.map((media) => (
                  <option key={media.dbId || media.id} value={media.dbId || media.id}>{formatMediaOptionLabel(media)}</option>
                ))}
              </select>
              <button
                onClick={() => saveFeaturedMedia('featuredMusic', featuredMusicId, 'Featured music')}
                style={{ padding: '10px 18px', borderRadius: '6px', border: 'none', backgroundColor: colors.warning, color: '#FFFFFF', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Set
              </button>
            </div>
          </div>

          {[0, 1, 2].map((slot) => (
            <div key={slot} style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Flame size={20} style={{ color: colors.warning }} />
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>Most Wanted #{slot + 2}</h3>
              </div>
              {selectedMostWanted[slot] && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: colors.bgTertiary, borderRadius: '6px', marginBottom: '8px' }}>
                  <Check size={16} style={{ color: colors.success }} />
                  <span style={{ fontSize: '13px', color: colors.success }}>
                    Selected: {selectedMostWanted[slot].title}{selectedMostWanted[slot].releaseYear ? ` (${selectedMostWanted[slot].releaseYear})` : ''}
                  </span>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '12px', alignItems: 'center' }}>
                <select
                  value={mostWantedIds[slot]}
                  onChange={(e) => setMostWantedIds(prev => prev.map((id, idx) => idx === slot ? e.target.value : id))}
                  style={{ width: '100%', minWidth: 0, padding: '10px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.bgTertiary, color: colors.text, fontSize: '13px' }}>
                  <option value="">Select Media</option>
                  {approvedMedia.map((media) => (
                    <option key={`${slot}-${media.dbId || media.id}`} value={media.dbId || media.id}>{formatMediaOptionLabel(media)}</option>
                  ))}
                </select>
                <button
                  onClick={() => saveMostWanted(slot, mostWantedIds[slot])}
                  style={{ padding: '10px 18px', borderRadius: '6px', border: 'none', backgroundColor: colors.warning, color: '#FFFFFF', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Set
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reaction of the Week Banner Management */}
      <div id="explore-reaction-section" style={{ ...sectionCardStyle, display: exploreSection === 'explore-reaction-section' ? 'block' : 'none' }}>
        <div style={{
          backgroundColor: colors.bgTertiary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Sparkles size={24} style={{ color: '#FFD700' }} />
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: colors.text, margin: 0 }}>
              Reaction of the Week Banner
            </h2>
          </div>
          <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: 0 }}>
            Program the three reaction-banner slots shown in Explore. Use this like a weekly content slate, not a loose link dump.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {/* Video Slot 1 */}
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `2px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '700'
              }}>1</div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
                Featured Video #1
              </h3>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: colors.textSecondary, marginBottom: '6px' }}>
                YouTube Video URL
              </label>
              <input
                type="text"
                value={reactionVideo1}
                onChange={(e) => setReactionVideo1(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '13px'
                }}
              />
            </div>
            <button
              onClick={() => saveReactionVideo(1, reactionVideo1)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
              Save Video #1
            </button>
          </div>

          {/* Video Slot 2 */}
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `2px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '700'
              }}>2</div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
                Featured Video #2
              </h3>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: colors.textSecondary, marginBottom: '6px' }}>
                YouTube Video URL
              </label>
              <input
                type="text"
                value={reactionVideo2}
                onChange={(e) => setReactionVideo2(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '13px'
                }}
              />
            </div>
            <button
              onClick={() => saveReactionVideo(2, reactionVideo2)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
              Save Video #2
            </button>
          </div>

          {/* Video Slot 3 */}
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `2px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '700'
              }}>3</div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
                Featured Video #3
              </h3>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: colors.textSecondary, marginBottom: '6px' }}>
                YouTube Video URL
              </label>
              <input
                type="text"
                value={reactionVideo3}
                onChange={(e) => setReactionVideo3(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '13px'
                }}
              />
            </div>
            <button
              onClick={() => saveReactionVideo(3, reactionVideo3)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
              Save Video #3
            </button>
          </div>
        </div>
      </div>

      <div id="explore-auto-carousel-section" style={{ ...sectionCardStyle, display: exploreSection === 'explore-auto-carousel-section' ? 'block' : 'none' }}>
        <div style={{
          backgroundColor: colors.bgTertiary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: colors.text, margin: '0 0 8px 0' }}>
                Auto Carousel Refresh
              </h2>
              <p style={{ color: colors.textSecondary, fontSize: '14px', margin: 0 }}>
                Scheduler runs 4 times daily. Auto banner refresh fires after every ingestion run, while manual editorial items remain first in sequence.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={refreshAutoCarouselManually}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Refresh Auto Picks Now
              </button>
              <button
                onClick={() => saveCarouselAutomationSettings({ enabled: !carouselAutomationSettings?.enabled })}
                disabled={savingCarouselSettings}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: carouselAutomationSettings?.enabled ? colors.success : colors.textTertiary,
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: savingCarouselSettings ? 'not-allowed' : 'pointer',
                  opacity: savingCarouselSettings ? 0.7 : 1
                }}
              >
                {carouselAutomationSettings?.enabled ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            padding: '14px 16px',
            borderRadius: '10px',
            backgroundColor: colors.bgTertiary,
            border: `1px solid ${colors.border}`,
            marginBottom: '18px'
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: colors.text, marginBottom: '4px' }}>
                Hero Media Poster
              </div>
              <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.4 }}>
                Show the movie/show poster next to the featured reaction in the homepage hero (clicking it opens that title's reactions).
                Posters are auto-matched from the reaction title; when OFF the hero peeks the next slide instead.
              </div>
            </div>
            <button
              onClick={() => saveCarouselAutomationSettings({ heroMediaPoster: carouselAutomationSettings?.heroMediaPoster === false })}
              disabled={savingCarouselSettings}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: carouselAutomationSettings?.heroMediaPoster !== false ? colors.success : colors.textTertiary,
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '700',
                cursor: savingCarouselSettings ? 'not-allowed' : 'pointer',
                opacity: savingCarouselSettings ? 0.7 : 1,
                flexShrink: 0
              }}
            >
              {carouselAutomationSettings?.heroMediaPoster !== false ? 'ON' : 'OFF'}
            </button>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            padding: '14px 16px',
            borderRadius: '10px',
            backgroundColor: colors.bgTertiary,
            border: `1px solid ${colors.border}`,
            marginBottom: '18px'
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: colors.text, marginBottom: '4px' }}>
                Minimum Reaction Length
              </div>
              <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.4 }}>
                Auto-picked carousel videos shorter than this are skipped, so Shorts posted as regular
                uploads don't get pulled in. Set to 0 to disable. Applies only to automatic picks —
                manually added carousel items are never filtered.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <input
                type="number"
                min="0"
                step="0.5"
                value={minDurationMinutesInput}
                onChange={(e) => setMinDurationMinutesInput(e.target.value)}
                style={{
                  width: '70px',
                  padding: '9px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgSecondary,
                  color: colors.text,
                  fontSize: '13px',
                  textAlign: 'center'
                }}
              />
              <span style={{ fontSize: '12px', color: colors.textSecondary }}>min</span>
              <button
                onClick={() => {
                  const minutes = parseFloat(minDurationMinutesInput);
                  const seconds = Number.isFinite(minutes) && minutes >= 0 ? Math.round(minutes * 60) : 240;
                  saveCarouselAutomationSettings({ minDurationSeconds: seconds });
                }}
                disabled={savingCarouselSettings}
                style={{
                  padding: '9px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: colors.purple500 || '#8B5CF6',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: savingCarouselSettings ? 'not-allowed' : 'pointer',
                  opacity: savingCarouselSettings ? 0.7 : 1
                }}
              >
                Save
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '18px' }}>
            {[
              { label: 'Eligible Channels', value: autoCarouselCount },
              { label: 'Auto Runs', value: 'All (1–4)' },
              { label: 'Auto Slots', value: carouselAutomationSettings?.maxAutoItems || 4 },
              { label: 'Last Trigger', value: carouselAutomationSettings?.lastTriggeredRun ? `#${carouselAutomationSettings.lastTriggeredRun}` : 'Not yet' }
            ].map((card) => (
              <div key={card.label} style={{ padding: '14px', borderRadius: '10px', backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{card.label}</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: colors.text }}>{card.value}</div>
              </div>
            ))}
          </div>

          <button
            onClick={() => setCarouselChannelListExpanded(v => !v)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgTertiary,
              color: colors.text,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: carouselChannelListExpanded ? '12px' : '0'
            }}
          >
            <span>Channel Eligibility List ({autoCarouselCount} eligible of {eligibleCarouselChannels.length})</span>
            <span style={{ fontSize: '18px', lineHeight: 1, transform: carouselChannelListExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
          </button>

          {carouselChannelListExpanded && (
            <>
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  value={carouselChannelSearch}
                  onChange={(e) => setCarouselChannelSearch(e.target.value)}
                  placeholder="Search channels to mark eligible for auto carousel..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.bgTertiary,
                    color: colors.text,
                    fontSize: '13px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {eligibleCarouselChannels.map((channel) => {
                  const channelKey = channel.id || channel.name;
                  const isSaving = updatingCarouselEligibility === channelKey;
                  return (
                    <div key={channelKey} style={{ padding: '14px', borderRadius: '10px', backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: colors.text, marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {channel.name}
                          </div>
                          <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                            {(channel.categories || ['general']).join(', ')} {channel.primaryCategory ? `• ${channel.primaryCategory}` : ''}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleChannelAutoEligibility(channel)}
                          disabled={isSaving}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '999px',
                            border: 'none',
                            backgroundColor: channel.autoCarouselEligible ? colors.success : colors.border,
                            color: channel.autoCarouselEligible ? '#FFFFFF' : colors.textSecondary,
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isSaving ? 'Saving...' : channel.autoCarouselEligible ? 'Eligible' : 'Not Eligible'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Carousel Items Management */}
      <div id="explore-carousel-section" style={{ ...sectionCardStyle, display: exploreSection === 'explore-carousel-section' ? 'block' : 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={sectionTitleStyle}>Carousel Item Queue</h2>
            <p style={{ color: colors.textSecondary, fontSize: '14px' }}>
              Manage the live homepage queue. Order is explicit, manual items are curated, and auto-picked entries are clearly marked.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={saveCarouselOrder}
              disabled={savingCarouselOrder}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '14px',
                fontWeight: '600',
                cursor: savingCarouselOrder ? 'not-allowed' : 'pointer'
              }}
            >
              {savingCarouselOrder ? 'Saving Order...' : 'Save Order'}
            </button>
            <button
              onClick={() => {
                setSelectedSearchChannel(null);
                setSearchModalOpen(true);
              }}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: colors.success,
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus size={16} />
              Add to Carousel
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { label: 'Total Queue Items', value: orderedCarouselItems.length },
            { label: 'Manual Editorial', value: manualCarouselItems.length },
            { label: 'Auto Inserted', value: autoCarouselItems.length }
          ].map((card) => (
            <div key={card.label} style={{ padding: '14px', borderRadius: '12px', backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{card.label}</div>
              <div style={{ fontSize: '22px', fontWeight: '800', color: colors.text }}>{card.value}</div>
            </div>
          ))}
        </div>

        {orderedCarouselItems.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
            marginBottom: '32px'
          }}>
            {orderedCarouselItems.map((item, index) => (
              <div key={item.id || index} style={{
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                padding: '16px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.accent}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = colors.border}
              >
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '80px',
                    height: '45px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    backgroundColor: colors.bgTertiary,
                    flexShrink: 0
                  }}>
                    {(item.videoThumbnail || item.channelThumbnail || item.imageUrl) && (
                      <img
                        src={item.videoThumbnail || item.channelThumbnail || item.imageUrl}
                        alt={item.videoTitle || item.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '999px',
                        fontSize: '10px',
                        fontWeight: '700',
                        backgroundColor: item.addedBy === 'auto-carousel' ? colors.info + '20' : colors.success + '20',
                        color: item.addedBy === 'auto-carousel' ? colors.info : colors.success
                      }}>
                        {item.addedBy === 'auto-carousel' ? 'AUTO' : 'MANUAL'}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '999px',
                        fontSize: '10px',
                        fontWeight: '700',
                        backgroundColor: colors.bgTertiary,
                        color: colors.textSecondary,
                        border: `1px solid ${colors.border}`
                      }}>
                        {item.type === 'video' ? 'Video' : 'Channel'}
                      </span>
                      <span style={{ fontSize: '11px', color: colors.textSecondary }}>
                        Order {item.order ?? index}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: colors.text,
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.videoTitle || item.title}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                      by {item.channelName} • {item.type === 'video' ? '🎥 Video' : '📺 Channel'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                  <input
                    type="number"
                    value={item.order ?? index}
                    onChange={(e) => updateCarouselOrderField(item.id || item._id, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.bgTertiary,
                      color: colors.text,
                      fontSize: '12px'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => moveCarouselItem(index, -1)}
                      disabled={index === 0}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.bgTertiary,
                        color: colors.text,
                        fontSize: '12px',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                        opacity: index === 0 ? 0.5 : 1
                      }}
                    >
                      Move Up
                    </button>
                    <button
                      onClick={() => moveCarouselItem(index, 1)}
                      disabled={index === carouselItems.length - 1}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.bgTertiary,
                        color: colors.text,
                        fontSize: '12px',
                        cursor: index === carouselItems.length - 1 ? 'not-allowed' : 'pointer',
                        opacity: index === carouselItems.length - 1 ? 0.5 : 1
                      }}
                    >
                      Move Down
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => window.open(item.youtubeUrl, '_blank')}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.bgTertiary,
                      color: colors.text,
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <ExternalLink size={14} />
                    View
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove "${item.videoTitle || item.title}" from carousel?`)) {
                        handleRemoveFromFeatured(item, 'carousel');
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: colors.error + '20',
                      color: colors.error,
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            marginBottom: '32px'
          }}>
            <Upload size={48} style={{ color: colors.textTertiary, marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              No Carousel Items
            </h3>
            <p style={{ fontSize: '14px', color: colors.textSecondary }}>
              Click "Add to Carousel" to search database and add videos or channels
            </p>
          </div>
        )}
      </div>

      {/* Featured Carousel Info Section */}
      <div id="explore-preview-section" style={{ ...sectionCardStyle, display: exploreSection === 'explore-preview-section' ? 'block' : 'none' }}>
        <h2 style={sectionTitleStyle}>
          Homepage Featured Reactors Preview
        </h2>
        <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '24px' }}>
          Operational preview of how the editorial ranking stack resolves on the homepage between search and the movies section.
        </p>

        {/* Current Carousel Preview */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '20px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '16px' }}>
            Currently Featured in Carousel
          </h3>

          <div style={{ display: 'grid', gap: '12px' }}>
            {/* Rank 1 - Channel of the Week */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: 'rgba(234, 179, 8, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(234, 179, 8, 0.3)'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#EAB308',
                color: '#1F2937',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0
              }}>1</div>
              <Award size={18} style={{ color: '#FFD700' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                  {channelOfWeek ? (featuredChannels || data.channels || []).find(ch => ch.id === channelOfWeek)?.name || (orderedCarouselItems[0]?.channelName || 'Not Set') : (orderedCarouselItems[0]?.channelName || 'Not Set')}
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Channel of the Week (Gold)</div>
              </div>
            </div>

            {/* Rank 2 - Rising Star */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: 'rgba(236, 72, 153, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(236, 72, 153, 0.3)'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#9CA3AF',
                color: '#1F2937',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0
              }}>2</div>
              <TrendingUp size={18} style={{ color: colors.success }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                  {risingStar ? (featuredChannels || data.channels || []).find(ch => ch.id === risingStar)?.name || (orderedCarouselItems[1]?.channelName || 'Not Set') : (orderedCarouselItems[1]?.channelName || 'Not Set')}
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Rising Star (Silver)</div>
              </div>
            </div>

            {/* Rank 3 - Most Talked About */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: colors.bgTertiary,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#CD7F32',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0
              }}>3</div>
              <Flame size={18} style={{ color: colors.warning }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                  {mostTalked ? (featuredChannels || data.channels || []).find(ch => ch.id === mostTalked)?.name || (orderedCarouselItems[2]?.channelName || 'Not Set') : (orderedCarouselItems[2]?.channelName || 'Not Set')}
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Most Talked About (Bronze)</div>
              </div>
            </div>

            {/* Rank 4 - Staff Pick */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: colors.bgTertiary,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: colors.textTertiary,
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0
              }}>4</div>
              <Star size={18} style={{ color: '#EC4899' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                  {staffPick ? (featuredChannels || data.channels || []).find(ch => ch.id === staffPick)?.name || (orderedCarouselItems[3]?.channelName || 'Not Set') : (orderedCarouselItems[3]?.channelName || 'Not Set')}
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Staff Pick (Gray)</div>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: colors.bgTertiary,
            borderRadius: '6px',
            border: `1px solid ${colors.border}`
          }}>
            <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0 }}>
              💡 <strong>Tip:</strong> Set the rankings above to automatically update the homepage carousel. Changes appear immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  };

  const ForumView = () => {
    // Filter threads based on filter and search
    const filteredThreads = useMemo(() => {
      let filtered = threads;

      // Apply filter
      if (threadFilter === 'pinned') {
        filtered = filtered.filter(t => t.isPinned);
      } else if (threadFilter === 'locked') {
        filtered = filtered.filter(t => t.isLocked);
      } else if (threadFilter === 'hidden') {
        filtered = filtered.filter(t => t.isHidden);
      }

      // Apply search
      if (threadSearch.trim()) {
        filtered = filtered.filter(t =>
          t.title?.toLowerCase().includes(threadSearch.toLowerCase()) ||
          t.content?.toLowerCase().includes(threadSearch.toLowerCase()) ||
          t.authorUsername?.toLowerCase().includes(threadSearch.toLowerCase())
        );
      }

      return filtered;
    }, [threads, threadFilter, threadSearch]);

    return (
      <div style={{ padding: '32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '8px' }}>
            Forum & Threads Management
          </h1>
          <p style={{ fontSize: '16px', color: colors.textSecondary }}>
            Moderate community discussions and threads
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '8px' }}>Total Threads</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: colors.accent }}>{threads.length}</div>
          </div>
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '8px' }}>Pinned</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: colors.warning }}>{threads.filter(t => t.isPinned).length}</div>
          </div>
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '8px' }}>Locked</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: colors.error }}>{threads.filter(t => t.isLocked).length}</div>
          </div>
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '8px' }}>Hidden</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: colors.textTertiary }}>{threads.filter(t => t.isHidden).length}</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['all', 'pinned', 'locked', 'hidden'].map(filter => (
              <button
                key={filter}
                onClick={() => setThreadFilter(filter)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: threadFilter === filter ? colors.accent : colors.bgSecondary,
                  color: threadFilter === filter ? '#FFFFFF' : colors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {filter}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={threadSearch}
            onChange={(e) => setThreadSearch(e.target.value)}
            placeholder="Search threads..."
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgSecondary,
              color: colors.text,
              fontSize: '14px',
              outline: 'none'
            }}
          />
          <button
            onClick={fetchThreads}
            disabled={threadsLoading}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: colors.accent,
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              cursor: threadsLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: threadsLoading ? 0.6 : 1
            }}
          >
            <RefreshCw size={16} style={{ animation: threadsLoading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>

        {/* Threads List */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {threadsLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px',
              color: colors.textSecondary
            }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
              Loading threads...
            </div>
          ) : filteredThreads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <MessageSquare size={48} style={{ color: colors.textTertiary, marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                No Threads Found
              </h3>
              <p style={{ fontSize: '14px', color: colors.textSecondary }}>
                {threadSearch ? 'Try adjusting your search' : 'Community threads will appear here'}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.bgTertiary }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase' }}>
                    Thread
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', width: '120px' }}>
                    Replies
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', width: '120px' }}>
                    Likes
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', width: '120px' }}>
                    Status
                  </th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', width: '180px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredThreads.map((thread, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderTop: `1px solid ${colors.border}`,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgTertiary}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '16px' }}>
                      <div style={{
                        fontWeight: '500',
                        color: colors.text,
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {thread.isPinned && <Pin size={14} style={{ color: colors.warning }} />}
                        {thread.isLocked && <Lock size={14} style={{ color: colors.error }} />}
                        {thread.isHidden && <EyeOff size={14} style={{ color: colors.textTertiary }} />}
                        {thread.title}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        by {thread.authorUsername} • {thread.category} • {new Date(thread.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center', fontSize: '16px', color: colors.textSecondary }}>
                      {thread.stats?.replies || 0}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center', fontSize: '16px', color: colors.success }}>
                      {thread.stats?.likes || 0}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: thread.isHidden ? colors.bgTertiary : thread.isLocked ? colors.error + '20' : thread.isPinned ? colors.warning + '20' : colors.success + '20',
                        color: thread.isHidden ? colors.textTertiary : thread.isLocked ? colors.error : thread.isPinned ? colors.warning : colors.success,
                        display: 'inline-block'
                      }}>
                        {thread.isHidden ? 'HIDDEN' : thread.isLocked ? 'LOCKED' : thread.isPinned ? 'PINNED' : 'ACTIVE'}
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => toggleThreadPin(thread._id, thread.isPinned)}
                          title={thread.isPinned ? 'Unpin' : 'Pin'}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            backgroundColor: thread.isPinned ? colors.warning + '20' : colors.bgTertiary,
                            color: thread.isPinned ? colors.warning : colors.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Pin size={14} />
                        </button>
                        <button
                          onClick={() => toggleThreadLock(thread._id, thread.isLocked)}
                          title={thread.isLocked ? 'Unlock' : 'Lock'}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            backgroundColor: thread.isLocked ? colors.error + '20' : colors.bgTertiary,
                            color: thread.isLocked ? colors.error : colors.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {thread.isLocked ? <Unlock size={14} /> : <Lock size={14} />}
                        </button>
                        <button
                          onClick={() => toggleThreadVisibility(thread._id, thread.isHidden)}
                          title={thread.isHidden ? 'Show' : 'Hide'}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            backgroundColor: thread.isHidden ? colors.textTertiary + '20' : colors.bgTertiary,
                            color: thread.isHidden ? colors.textTertiary : colors.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {thread.isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>
                        <button
                          onClick={() => deleteThread(thread._id)}
                          title="Delete"
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: colors.error + '20',
                            color: colors.error,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const ReportsView = () => (
    <div style={{ padding: '32px' }}>
      <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '32px' }}>
        Reports & Moderation
      </h1>

      <div style={{
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: '24px'
      }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Flag size={48} style={{ color: colors.textTertiary, marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Reports Yet
          </h3>
          <p style={{ fontSize: '14px', color: colors.textSecondary }}>
            Flagged content will appear here for moderation
          </p>
        </div>
      </div>
    </div>
  );

  const PeakMomentsView = () => {
    const [alert, setAlert] = useState(null);

    return (
      <div style={{ padding: '32px', backgroundColor: colors.bg }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '32px' }}>
          Peak Moments Management
        </h1>
        <AdminPeakMomentsTab setAlert={setAlert} />
        {alert && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 24px',
            backgroundColor: alert.type === 'success' ? colors.success : colors.error,
            color: '#FFFFFF',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999
          }}>
            {alert.message}
          </div>
        )}
      </div>
    );
  };

  const VideoIngestionView = () => {
    // Handler for individual channel ingestion actions
    const handleIngestAction = async (channelId, mode, channelName = 'Selected Channel') => {
      setAlert({
        type: 'info',
        message: `Requesting ${mode === 'all' ? 'FULL video ingest' : 'NEW video fetch'} for ${channelName}. This is a background task. Refresh status later.`
      });

      try {
        if (mode === 'all') {
          await adminAPI.ingestAllVideosForChannel(channelId);
        } else {
          await adminAPI.fetchNewVideosForChannel(channelId);
        }

        setTimeout(() => {
          setAlert({ type: 'info', message: `Task for ${channelName} sent. Refreshing status...` });
          fetchIngestionStatusData();
        }, 1500);
      } catch (error) {
        console.error('Ingestion error:', error);
        setAlert({ type: 'error', message: 'Failed to start ingestion: ' + handleAPIError(error) });
      }
    };

    // Handler for bulk ingestion actions
    const handleBulkIngest = async (mode) => {
      if (selectedIngestionChannelIds.size === 0) {
        setAlert({ type: 'info', message: 'No channels selected for bulk action.' });
        return;
      }

      const ids = Array.from(selectedIngestionChannelIds);
      setAlert({
        type: 'info',
        message: `Requesting bulk ${mode === 'all' ? 'FULL ingest' : 'NEW video fetch'} for ${ids.length} channels. This is a background task.`
      });

      try {
        await adminAPI.bulkManageVideos(ids, mode);
        setSelectedIngestionChannelIds(new Set());

        setTimeout(() => {
          setAlert({ type: 'info', message: `Bulk task for ${ids.length} channels sent. Refreshing status...` });
          fetchIngestionStatusData();
        }, 1500);
      } catch (error) {
        console.error('Bulk ingestion error:', error);
        setAlert({ type: 'error', message: 'Failed to start bulk ingestion: ' + handleAPIError(error) });
      }
    };

    // Toggle channel selection
    const toggleIngestionChannelSelection = (channelId) => {
      setSelectedIngestionChannelIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(channelId)) {
          newSet.delete(channelId);
        } else {
          newSet.add(channelId);
        }
        return newSet;
      });
    };

    // Select/deselect all channels
    const toggleSelectAll = () => {
      if (selectedIngestionChannelIds.size === ingestionChannels.length) {
        setSelectedIngestionChannelIds(new Set());
      } else {
        setSelectedIngestionChannelIds(new Set(ingestionChannels.map(ch => ch.channelId)));
      }
    };

    return (
      <div style={{ padding: '32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '8px' }}>
            Video Ingestion Management
          </h1>
          <p style={{ fontSize: '16px', color: colors.textSecondary }}>
            Manage and sync video data from whitelisted YouTube channels
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button
            onClick={() => fetchIngestionStatusData()}
            disabled={ingestionLoading}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: colors.accent,
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              cursor: ingestionLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: ingestionLoading ? 0.6 : 1
            }}
          >
            <RefreshCw size={16} style={{ animation: ingestionLoading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh Status
          </button>

          {selectedIngestionChannelIds.size > 0 && (
            <>
              <button
                onClick={() => handleBulkIngest('all')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgSecondary,
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Download size={16} />
                Bulk Ingest All ({selectedIngestionChannelIds.size})
              </button>
              <button
                onClick={() => handleBulkIngest('new')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgSecondary,
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <RefreshCw size={16} />
                Bulk Fetch New ({selectedIngestionChannelIds.size})
              </button>
            </>
          )}
        </div>

        {/* Ingestion Table */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {ingestionLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px',
              color: colors.textSecondary
            }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
              Loading ingestion status...
            </div>
          ) : ingestionChannels.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Database size={48} style={{ color: colors.textTertiary, marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                No Channels Found
              </h3>
              <p style={{ fontSize: '14px', color: colors.textSecondary }}>
                Add channels to the whitelist to start ingesting videos
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.bgTertiary }}>
                  <th style={{
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: colors.textSecondary,
                    width: '50px'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedIngestionChannelIds.size === ingestionChannels.length && ingestionChannels.length > 0}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase' }}>
                    Channel
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase' }}>
                    Videos Ingested
                  </th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase' }}>
                    Last Fetched
                  </th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {ingestionChannels.map((channel, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderTop: `1px solid ${colors.border}`,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgTertiary}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIngestionChannelIds.has(channel.channelId)}
                        onChange={() => toggleIngestionChannelSelection(channel.channelId)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{
                        fontWeight: '500',
                        color: colors.text,
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {channel.channelName}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: colors.textSecondary,
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {channel.channelId}
                      </div>
                    </td>
                    <td style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: channel.videosIngested > 0 ? colors.success : colors.textSecondary,
                      fontWeight: channel.videosIngested > 0 ? '600' : 'normal',
                      fontSize: '16px'
                    }}>
                      {channel.videosIngested || 0}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: colors.textSecondary }}>
                      {channel.lastFetched ? new Date(channel.lastFetched).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleIngestAction(channel.channelId, 'all', channel.channelName)}
                          title="Ingest all videos for this channel"
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            backgroundColor: colors.bgTertiary,
                            color: colors.text,
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Download size={14} />
                          Ingest All
                        </button>
                        <button
                          onClick={() => handleIngestAction(channel.channelId, 'new', channel.channelName)}
                          title="Fetch new videos for this channel"
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            backgroundColor: colors.bgTertiary,
                            color: colors.text,
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <RefreshCw size={14} />
                          Fetch New
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Info Card */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            💡 Video Ingestion Guide
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: colors.textSecondary, lineHeight: '1.8' }}>
            <li><strong>Ingest All:</strong> Fetches and stores ALL videos from the channel (may take time for large channels)</li>
            <li><strong>Fetch New:</strong> Only fetches videos published since the last sync (recommended for regular updates)</li>
            <li><strong>Bulk Actions:</strong> Select multiple channels using checkboxes to perform batch operations</li>
            <li><strong>Background Processing:</strong> All ingestion tasks run in the background. Refresh status to see updates.</li>
          </ul>
        </div>
      </div>
    );
  };

  // Database Search Modal for Carousel
  const DatabaseSearchModal = () => {
    if (!searchModalOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={() => setSearchModalOpen(false)}
      >
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '32px'
        }}
        onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: colors.text, margin: 0 }}>
              Add to Carousel - Search Database
            </h2>
            <button
              onClick={() => setSearchModalOpen(false)}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                cursor: 'pointer'
              }}
            >
              <CloseIcon size={20} />
            </button>
          </div>

          {/* Search by URL */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              Search by YouTube Video URL
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="Paste YouTube video URL here..."
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '14px',
                  outline: 'none'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') searchVideosByURL();
                }}
              />
              <button
                onClick={searchVideosByURL}
                disabled={searching || !urlInput.trim()}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: searching || !urlInput.trim() ? colors.textSecondary : colors.accent,
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: searching || !urlInput.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: searching || !urlInput.trim() ? 0.6 : 1
                }}
              >
                {searching ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={16} />}
                Search
              </button>
            </div>
          </div>

          {/* Video Results */}
          {videoResults.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
                Found {videoResults.length} video(s)
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                {videoResults.map((video, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: colors.bgTertiary,
                    borderRadius: '8px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '120px',
                      height: '68px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: colors.border,
                      flexShrink: 0
                    }}>
                      {video.thumbnail && (
                        <img
                          src={video.thumbnail}
                          alt={video.title || video.videoTitle}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>
                        {video.title || video.videoTitle}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {video.channelName || video.channel?.name}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddVideoToCarousel(video)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: colors.success,
                        color: '#FFFFFF',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Add to Carousel
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div style={{
            marginTop: '24px',
            padding: '12px',
            backgroundColor: colors.bgTertiary,
            borderRadius: '8px',
            fontSize: '13px',
            color: colors.textSecondary
          }}>
            💡 <strong>Tip:</strong> Paste a YouTube video URL to search the database. Videos must be ingested first via the Video Ingestion tab.
          </div>
        </div>
      </div>
    );
  };

  // Channel Modal (Add/Edit)
  const ChannelModal = () => {
    const [editCategories, setEditCategories] = useState(selectedChannel?.categories || ['general']);
    const [editPrimary, setEditPrimary] = useState(selectedChannel?.primaryCategory || 'general');
    const [editReactorType, setEditReactorType] = useState(selectedChannel?.reactorType || 'solo');
    const [channelIdInput, setChannelIdInput] = useState('');
    const [customNameInput, setCustomNameInput] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const CATEGORIES = {
      'movies': { label: 'Movies', color: '#8B5CF6' },
      'tv': { label: 'TV Shows', color: '#10B981' },
      'anime': { label: 'Anime', color: '#EF4444' },
      'music': { label: 'Music', color: '#F59E0B' },
      'gaming': { label: 'Gaming', color: '#3B82F6' },
      'general': { label: 'General', color: '#6B7280' }
    };

    const handleSubmit = async () => {
      setSubmitting(true);
      try {
        if (selectedChannel) {
          // Edit existing channel
          await editChannel(selectedChannel.name, {
            categories: editCategories,
            primaryCategory: editPrimary,
            reactorType: editReactorType
          });
        } else {
          // Add new channel
          if (!channelIdInput.trim()) {
            setAlert({ type: 'error', message: 'Please enter a channel ID' });
            setSubmitting(false);
            return;
          }
          await addNewChannel(channelIdInput, {
            customName: customNameInput || null,
            categories: editCategories,
            primaryCategory: editPrimary,
            listType: 'whitelist',
            reactorType: editReactorType
          });
        }
        setModalOpen(false);
      } catch (error) {
        setAlert({ type: 'error', message: handleAPIError(error) });
      } finally {
        setSubmitting(false);
      }
    };

    if (!modalOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={() => setModalOpen(false)}
      >
        <div
          style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '32px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: colors.text, margin: 0 }}>
              {selectedChannel ? 'Edit Channel' : 'Add New Channel'}
            </h2>
            <button
              onClick={() => setModalOpen(false)}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                cursor: 'pointer'
              }}
            >
              <CloseIcon size={20} />
            </button>
          </div>

          {/* Channel ID (only for new channels) */}
          {!selectedChannel && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                YouTube Channel ID *
              </label>
              <input
                type="text"
                value={channelIdInput}
                onChange={(e) => setChannelIdInput(e.target.value)}
                placeholder="UCxxxxxxxxxxxxxxxxxxxxxx"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          )}

          {/* Custom Name (only for new channels) */}
          {!selectedChannel && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                Custom Name (Optional)
              </label>
              <input
                type="text"
                value={customNameInput}
                onChange={(e) => setCustomNameInput(e.target.value)}
                placeholder="Leave empty to use channel's real name"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgTertiary,
                  color: colors.text,
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          )}

          {/* Reactor Type */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              Reactor Type
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {[
                { value: 'solo', label: '🎯 Solo Vibes' },
                { value: 'duo', label: '💑 Reacting Together' },
                { value: 'group', label: '👥 Group Energy' },
                { value: 'family', label: '👨‍👩‍👧‍👦 Family Feels' }
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => setEditReactorType(type.value)}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    border: `2px solid ${editReactorType === type.value ? colors.accent : colors.border}`,
                    backgroundColor: editReactorType === type.value ? colors.accent + '20' : colors.bgTertiary,
                    color: editReactorType === type.value ? colors.accent : colors.text,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              Content Categories
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(CATEGORIES).map(([key, cat]) => {
                const isSelected = editCategories.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (isSelected && editCategories.length > 1) {
                        setEditCategories(prev => prev.filter(c => c !== key));
                        if (editPrimary === key) {
                          setEditPrimary(editCategories.find(c => c !== key));
                        }
                      } else if (!isSelected) {
                        setEditCategories(prev => [...prev, key]);
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: `1px solid ${isSelected ? cat.color : colors.border}`,
                      backgroundColor: isSelected ? cat.color + '20' : colors.bgTertiary,
                      color: isSelected ? cat.color : colors.textSecondary,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Primary Category */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              Primary Category
            </label>
            <select
              value={editPrimary}
              onChange={(e) => setEditPrimary(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {editCategories.map(cat => (
                <option key={cat} value={cat}>{CATEGORIES[cat]?.label || cat}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setModalOpen(false)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgTertiary,
                color: colors.text,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: submitting ? colors.textSecondary : colors.accent,
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {submitting ? (
                <>
                  <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  {selectedChannel ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {selectedChannel ? 'Update Channel' : 'Add Channel'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="reactorhub-admin" style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: colors.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Sidebar />
      <div className="reactorhub-admin-main" style={{ marginLeft: isMobile ? 0 : '260px', paddingTop: isMobile ? '76px' : 0, paddingBottom: isMobile ? '24px' : 0, flex: 1, minHeight: '100vh', minWidth: 0 }}>
        {activeTab === 'dashboard' && <DashboardView />}
        {activeTab === 'channels' && (
          <ChannelsView
            channels={data.channels || []}
            colors={colors}
            onAdd={() => { setNewChannelId(''); setCustomName(''); setSelectedChannel(null); setModalOpen(true); }}
            onEdit={(channel) => { setSelectedChannel(channel); setModalOpen(true); }}
            onRemove={removeChannel}
          />
        )}
        {activeTab === 'peak-moments' && <PeakMomentsView />}
        {activeTab === 'peak-candidates' && <AdminPeakCandidatesTab colors={colors} showAlert={setAlert} />}
        {activeTab === 'video-ingestion' && <VideoIngestionView />}
        {activeTab === 'media-content' && <AdminMediaContentTab colors={colors} showAlert={setAlert} />}
        {activeTab === 'featured' && <FeaturedView />}
        {activeTab === 'voting' && <AdminVotingTab />}
        {activeTab === 'forum' && <ForumView />}
        {activeTab === 'reports' && <ReportsView />}
        {activeTab === 'creators' && <AdminCreatorsTab />}
        {activeTab === 'platform-analytics' && <AdminPlatformAnalyticsTab />}
        {activeTab === 'boosts' && <AdminBoostsView colors={colors} showAlert={setAlert} />}
        {activeTab === 'testing' && <AdminTestingPanel />}
      </div>

      {/* Global Alert */}
      {alert && (
        <div className="reactorhub-admin-alert" style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          minWidth: '320px',
          maxWidth: '500px',
          padding: '16px 20px',
          borderRadius: '12px',
          backgroundColor: alert.type === 'success' ? colors.success : alert.type === 'error' ? colors.error : colors.info,
          color: '#FFFFFF',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            {alert.type === 'success' && <CheckCircle size={20} />}
            {alert.type === 'error' && <AlertCircle size={20} />}
            {alert.type === 'info' && <AlertCircle size={20} />}
            <span style={{ fontSize: '14px', fontWeight: '500' }}>{alert.message}</span>
          </div>
          <button
            onClick={() => setAlert(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 0.8}
          >
            <CloseIcon size={18} />
          </button>
        </div>
      )}

      {/* Database Search Modal */}
      <DatabaseSearchModal />

      {/* Channel Add/Edit Modal */}
      <ChannelModal />

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @media (max-width: 768px) {
            .reactorhub-admin {
              width: 100%;
              overflow-x: hidden;
            }

            .reactorhub-admin-main {
              width: 100%;
              overflow-x: hidden;
            }

            .reactorhub-admin-main > div {
              padding: 16px !important;
              min-height: auto !important;
            }

            .reactorhub-admin-main h1 {
              font-size: 22px !important;
              line-height: 1.2 !important;
            }

            .reactorhub-admin-main h2 {
              font-size: 20px !important;
              line-height: 1.25 !important;
            }

            .reactorhub-admin-main h3 {
              font-size: 16px !important;
            }

            .reactorhub-admin-main [style*="grid-template-columns"] {
              grid-template-columns: 1fr !important;
            }

            .reactorhub-admin-main [style*="display: flex"] {
              max-width: 100%;
            }

            .reactorhub-admin-main button,
            .reactorhub-admin-main input,
            .reactorhub-admin-main select,
            .reactorhub-admin-main textarea {
              max-width: 100%;
              box-sizing: border-box;
            }

            .reactorhub-admin-main table {
              min-width: 720px;
            }

            .reactorhub-admin-main [style*="overflow: auto"],
            .reactorhub-admin-main [style*="overflow-x: auto"] {
              -webkit-overflow-scrolling: touch;
            }

            .reactorhub-admin-alert {
              top: calc(88px + env(safe-area-inset-top)) !important;
              left: 12px !important;
              right: 12px !important;
              min-width: 0 !important;
              max-width: none !important;
              padding: 12px 14px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ModernAdminDashboard;
